// pages/NewAnalysisPage.jsx
//
// Unified /brands/:brandId/new-analysis flow for the Analysis Run lifecycle.
// Four sequenced stages with explicit checkpoint gates:
//
//   1. Configuration  -> Run Audit
//   2. Audit Review   -> Generate Analysis
//   3. Analysis Review-> Generate PPTX (or Revise)
//   4. Deliverable    -> Mark Delivered
//
// Each stage corresponds to a set of AnalysisRun statuses; the wizard reads
// run.status to decide which stage to render. Approval buttons transition the
// status via /analysis-runs/{id}/status and advance the stepper.
//
// Lock acquisition follows the late-lock pattern: no upfront acquire. The
// first server-side write (audit creation) auto-acquires via enforce_brand_lock.
// If another user holds an active lock, we surface the 409 as a read-only
// banner with holder info.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import Toast from "../components/Toast";
import CopilotDrawer from "../components/CopilotDrawer";
import "../components/theme.css";
import "../components/toast.css";

import {
  fetchBrands,
  fetchBrandAliases,
  addBrandAlias,
  fetchQuestionsForBrand,
  uploadCSV,
  analysisRunSetDraftQuestions,
  brandQuestionsOverwrite,
  updateQuestion,
  fetchSearchApiUsage,
  fetchAuditMatrix,
  fetchRunStatus,
  questionTemplatesList,
  questionTemplateGet,
  modelTierTemplatesList,
  analysisRunCreate,
  analysisRunPreviewTitle,
  analysisRunGet,
  analysisRunsActiveByBrand,
  analysisRunTransitionStatus,
  analysisRunProgress,
  getRunArchitecture,
  saveRunArchitecture,
  analysisRunPptxLatest,
  analysisRunAliasPreflight,
  analysisRunSetAliasesPreflight,
  analysisRunSetFocusSelection,
  analysisRunRevise,
  analysisRunLinkAudit,
  analysisRunPptxDownload,
  analysisRunPptxArtifacts,
  analysisRunPptxVersionDownload,
  createAudit,
  startRun,
} from "../services/api";
import AuditMatrix from "../components/AuditMatrix";


// Provider-grouped model catalog for the Custom picker. Also lives in
// TemplatesPage - kept in sync manually. A shared module is a future polish
// if drift becomes real.
const MODEL_GROUPS = [
  {
    provider: "OpenAI",
    models: [
      { id: "gpt-4o", label: "gpt-4o" },
      { id: "gpt-4o-mini", label: "gpt-4o-mini" },
      { id: "gpt-4.1-mini", label: "gpt-4.1-mini" },
      { id: "o4-mini", label: "o4-mini" },
      { id: "gpt-5", label: "gpt-5" },
      { id: "gpt-5-mini", label: "gpt-5-mini" },
    ],
  },
  {
    provider: "Perplexity",
    models: [
      { id: "sonar", label: "sonar" },
      { id: "sonar-pro", label: "sonar-pro" },
      { id: "sonar-reasoning", label: "sonar-reasoning" },
    ],
  },
  {
    provider: "Gemini",
    models: [
      { id: "gemini-2.5-flash", label: "gemini-2.5-flash" },
      { id: "gemini-2.5-flash-lite", label: "gemini-2.5-flash-lite" },
      { id: "gemini-2.5-pro", label: "gemini-2.5-pro" },
    ],
  },
  {
    provider: "Claude (Anthropic)",
    models: [
      { id: "claude-sonnet-4-6", label: "claude-sonnet-4-6" },
      { id: "claude-opus-4-6", label: "claude-opus-4-6" },
      { id: "claude-haiku-4-5", label: "claude-haiku-4-5" },
    ],
  },
  {
    provider: "Google AI Overview",
    models: [{ id: "searchapi-ai-overview", label: "SearchAPI (AI Overview)" }],
  },
  {
    // AI Mode is Google's opt-in conversational search surface - different
    // from AIO, which appears opportunistically on the SERP. Kept as a
    // separate provider so the audit matrix shows it in its own column
    // and the two visibility signals stay distinguishable.
    provider: "Google AI Mode",
    models: [{ id: "searchapi-ai-mode", label: "SearchAPI (AI Mode)" }],
  },
];


// Stage routing: clicking Run Audit should immediately move the user to
// Stage 2, where the matrix renders empty and fills in live as runs execute.
// Stage 2 owns both running_audit and audit_complete so the stepper
// advances the moment the backend transitions to running_audit.
// The Validation step (3) is a sub-view of the audit_complete status - no dedicated
// backend status. It sits between the audit matrix review and starting the analysis:
// the user reviews/edits the AI-detected brand + competitor architecture that drives
// every metric, then clicks Generate Analysis.
const STAGES = [
  { key: "configure", label: "1. Analysis Setup", statuses: ["pending"] },
  { key: "audit", label: "2. Audit", statuses: ["running_audit", "audit_complete"] },
  { key: "validation", label: "3. Validation", statuses: [] },
  { key: "analysis", label: "4. Analysis Review", statuses: ["running_analysis", "analysis_complete"] },
  { key: "deliverable", label: "5. Deliverable", statuses: ["generating_pptx", "ready", "delivered"] },
];

const FAILED_STATUS = "failed";


function stageForStatus(status) {
  for (const s of STAGES) {
    if (s.statuses.includes(status)) return s.key;
  }
  return "configure";
}


export default function NewAnalysisPage({ currentUser }) {
  const { brandId: brandIdParam } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const brandId = Number(brandIdParam);
  const initialRunId = search.get("runId") ? Number(search.get("runId")) : null;

  const [brand, setBrand] = useState(null);
  const [allBrands, setAllBrands] = useState([]);
  const [runId, setRunId] = useState(initialRunId);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // FB006 - sub-brand selection (the "audited brand"). Empty = master.
  // The Brand Universe in the report ALWAYS aggregates master + all
  // sub-brands regardless of this choice; the dialog explains that.
  const [focusSubBrandIds, setFocusSubBrandIds] = useState([]);
  const [focusConfirmOpen, setFocusConfirmOpen] = useState(false);
  const [focusConfirmedOnce, setFocusConfirmedOnce] = useState(false);

  const showToast = (message, type = "success") => setToast({ message, type });

  const loadBrand = useCallback(async () => {
    try {
      const list = await fetchBrands();
      setAllBrands(list || []);
      const b = (list || []).find((x) => Number(x.id) === brandId);
      setBrand(b || null);
    } catch {
      setBrand(null);
    }
  }, [brandId]);

  // Sub-brands directly under the current master. Used by the wizard's
  // "audited brand" selector (FB006).
  const subBrands = useMemo(
    () =>
      (allBrands || []).filter(
        (b) =>
          b.brand_type === "sub" &&
          Number(b.parent_brand_id) === Number(brandId),
      ),
    [allBrands, brandId],
  );

  const reloadRun = useCallback(async () => {
    if (!runId) return;
    try {
      const r = await analysisRunGet(runId);
      setRun(r);
    } catch (e) {
      showToast(e?.message || "Failed to load analysis run", "error");
    }
  }, [runId]);

  // Initial load only. Intentionally does NOT depend on `runId` - when the
  // wizard creates a run mid-flow (e.g. alias pre-flight needs a run_id),
  // setRunId + setRun update state in place; children can call onReload()
  // explicitly if they need a fresh server copy. A runId-dependent effect
  // here would re-fire, flip loading=true, unmount ConfigureStage and wipe
  // its local state (alias proposals, modal open flag, draft questions).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadBrand();
      if (initialRunId && !cancelled) {
        try {
          const r = await analysisRunGet(initialRunId);
          if (!cancelled) {
            setRun(r);
            // FB006 - rehydrate the focus selection so a reload mid-run
            // keeps the chosen sub-brands in the UI.
            const ids = Array.isArray(r?.focus_sub_brand_ids)
              ? r.focus_sub_brand_ids.map(Number).filter(Boolean)
              : [];
            setFocusSubBrandIds(ids);
            if (ids.length > 0) setFocusConfirmedOnce(true);
          }
        } catch (e) {
          if (!cancelled) showToast(e?.message || "Failed to load analysis run", "error");
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL in sync with the current runId so browser-back from the
  // analysis report page (or any other navigation away) lands the user back
  // on the SAME run instead of fresh Stage 1. Uses replaceState so we don't
  // pollute history with one entry per state change.
  useEffect(() => {
    if (!runId) return;
    if (Number(initialRunId) === Number(runId)) return;
    navigate(`/brands/${brandId}/new-analysis?runId=${runId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Auto-resume: when the user lands on the wizard with NO runId in the URL
  // (e.g. clicked "New analysis" from the brands hub or pasted the wizard
  // URL without a query string), check if there's already an active run
  // for this brand. If yes, redirect to that run's URL so the user picks
  // up exactly where they left off. The user can still click "+ Start a
  // fresh run" inside the wizard if they actually wanted a new one.
  //
  // Without this, leaving Stage 3 mid-stream and coming back via fresh
  // navigation would silently start the wizard from Stage 1 instead of
  // resuming the existing run - the bug the user reported as "Stage 3
  // restarts from step 1 after I leave and come back".
  useEffect(() => {
    if (initialRunId) return; // URL already has a runId, nothing to do
    if (loading) return;
    if (!brand) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await analysisRunsActiveByBrand();
        if (cancelled) return;
        const items = data?.items || [];
        const forBrand = items.find((row) => Number(row.brand_id) === brandId);
        if (!forBrand || !forBrand.runs || forBrand.runs.length === 0) return;
        // Pick the most recent active run (the API returns them sorted -
        // fall back to max id just in case).
        const runs = forBrand.runs.slice().sort((a, b) => Number(b.id) - Number(a.id));
        const target = runs[0];
        if (!target) return;
        navigate(`/brands/${brandId}/new-analysis?runId=${target.id}`, {
          replace: true,
        });
      } catch {
        // Soft-fail: if the lookup errors, the wizard just starts fresh.
      }
    })();
    return () => { cancelled = true; };
    // Run once after the initial load resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, brand]);

  // FB006 - when an existing pending run is loaded, keep its
  // focus_sub_brand_ids in sync with the UI selection. We only PATCH
  // when the run exists AND is still in 'pending' (the backend rejects
  // the change after that). Skip the initial render where we just
  // rehydrated from the run.
  useEffect(() => {
    if (!run?.id) return;
    if (run.status !== "pending") return;
    const current = (run.focus_sub_brand_ids || []).map(Number).sort();
    const next = [...focusSubBrandIds].map(Number).sort();
    if (current.length === next.length && current.every((v, i) => v === next[i])) {
      return; // no change
    }
    let cancelled = false;
    (async () => {
      try {
        await analysisRunSetFocusSelection(run.id, focusSubBrandIds);
        if (!cancelled) {
          setRun((prev) =>
            prev
              ? { ...prev, focus_sub_brand_ids: focusSubBrandIds }
              : prev,
          );
        }
      } catch (e) {
        // Don't toast - the user can still see the chip state, and the
        // selection is sent again on Submit if backend was unreachable.
        // (Surfacing this would be noisy on transient network blips.)
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSubBrandIds, run?.id, run?.status]);

  // Top-level polling: when a run is at a backend auto-advance gate state
  // (audit_complete, analysis_complete) AND auto_advance is on, the BACKEND
  // is moving the run forward via on_status_changed in a BackgroundTask.
  // The wizard has stage-specific polling for `running_audit` (Stage 2's
  // SRA-progress poll) and `running_analysis` (Stage 3's poll), but nothing
  // for the "between" gate states. Without this poll, the wizard sits on
  // Stage 2 looking at the matrix while the backend has already started
  // Stage 3 - the user has to refresh to unstick.
  useEffect(() => {
    if (!run || !run.auto_advance) return;
    const POLLABLE = ["audit_complete", "analysis_complete", "generating_pptx"];
    if (!POLLABLE.includes(run.status)) return;
    const id = setInterval(() => {
      reloadRun();
    }, 3000);
    return () => clearInterval(id);
  }, [run?.status, run?.auto_advance, reloadRun]);

  // Retained as a pass-through for call-sites that still use it defensively.
  // Originally detected brand_locked 409s; brand-locking has been retired in
  // favour of run-scoped drafts + explicit library overwrite (see the pivot
  // away from LLM146/170/174/175/185).
  const handle409 = () => false;

  // Validation is a sub-view of audit_complete; this local flag toggles between
  // the audit matrix (step 2) and the architecture-validation view (step 3).
  const [validationOpen, setValidationOpen] = useState(false);
  let currentStage = run ? stageForStatus(run.status) : "configure";
  if (currentStage === "audit" && run?.status === "audit_complete" && validationOpen) {
    currentStage = "validation";
  }
  const isFailed = run?.status === FAILED_STATUS;

  if (loading) {
    return (
      <AppShell currentUser={currentUser}>
        <div style={styles.page}>
          <div style={styles.loading}>Loading…</div>
        </div>
      </AppShell>
    );
  }

  if (!brand) {
    return (
      <AppShell currentUser={currentUser}>
        <div style={styles.page}>
          <div style={styles.error}>Brand not found or not accessible.</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell currentUser={currentUser}>
      <div style={styles.page}>
        <PageHeader brand={brand} run={run} onBack={() => navigate("/analyses")} />

        <Stepper currentStage={currentStage} failed={isFailed} />

        {isFailed ? (
          <FailedCard run={run} onReload={reloadRun} />
        ) : currentStage === "configure" ? (
          <ConfigureStage
            brand={brand}
            run={run}
            setRunId={setRunId}
            setRun={setRun}
            showToast={showToast}
            handle409={handle409}
            onReload={reloadRun}
            subBrands={subBrands}
            focusSubBrandIds={focusSubBrandIds}
            setFocusSubBrandIds={setFocusSubBrandIds}
            focusConfirmOpen={focusConfirmOpen}
            setFocusConfirmOpen={setFocusConfirmOpen}
            focusConfirmedOnce={focusConfirmedOnce}
            setFocusConfirmedOnce={setFocusConfirmedOnce}
          />
        ) : currentStage === "audit" ? (
          <AuditStage run={run} brand={brand} onReload={reloadRun} showToast={showToast} handle409={handle409}
            onProceedToValidation={() => setValidationOpen(true)} />
        ) : currentStage === "validation" ? (
          <ValidationStage run={run} brand={brand} onReload={reloadRun} showToast={showToast} handle409={handle409}
            onBack={() => setValidationOpen(false)} />
        ) : currentStage === "analysis" ? (
          <AnalysisStage run={run} brand={brand} onReload={reloadRun} showToast={showToast} handle409={handle409} />
        ) : (
          <DeliverableStage run={run} brand={brand} onReload={reloadRun} showToast={showToast} />
        )}

        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      </div>
    </AppShell>
  );
}


// -----------------------------------------------------------
// Header + Stepper + shared chrome
// -----------------------------------------------------------

function PageHeader({ brand, run, onBack }) {
  const metaParts = [];
  if (brand.country) metaParts.push(brand.country);
  if (brand.industry) metaParts.push(brand.industry);
  if (run) metaParts.push(`Run #${run.id}`);

  return (
    <div style={styles.header}>
      <button onClick={onBack} style={styles.backBtn} type="button">
        <span style={{ fontSize: 14, lineHeight: 1 }}>&larr;</span>
        <span>Analysis Hub</span>
      </button>
      <div style={{ marginTop: 14 }}>
        <h1 style={styles.title}>
          New Analysis <span style={styles.titleDivider}>·</span>{" "}
          <span style={styles.titleBrand}>{brand.name}</span>
        </h1>
        {(metaParts.length > 0 || run) && (
          <div style={styles.subtitle}>
            {metaParts.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={styles.subtitleDot}>·</span>}
                <span>{p}</span>
              </React.Fragment>
            ))}
            {run && (
              <>
                {metaParts.length > 0 && <span style={styles.subtitleDot}>·</span>}
                <StatusChip status={run.status} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}


function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}


function StatusChip({ status }) {
  const colors = {
    pending: { bg: "#f3f4f6", fg: "#374151" },
    running_audit: { bg: "#fef3c7", fg: "#92400e" },
    audit_complete: { bg: "#dbeafe", fg: "#1e40af" },
    running_analysis: { bg: "#fef3c7", fg: "#92400e" },
    analysis_complete: { bg: "#dbeafe", fg: "#1e40af" },
    generating_pptx: { bg: "#fef3c7", fg: "#92400e" },
    ready: { bg: "#d1fae5", fg: "#065f46" },
    delivered: { bg: "#d1fae5", fg: "#065f46" },
    failed: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const c = colors[status] || colors.pending;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 11,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}


function Stepper({ currentStage, failed }) {
  const currentIdx = STAGES.findIndex((s) => s.key === currentStage);
  return (
    <div style={styles.stepper}>
      {STAGES.map((s, idx) => {
        const done = idx < currentIdx && !failed;
        const active = idx === currentIdx && !failed;
        return (
          <React.Fragment key={s.key}>
            <div style={active ? styles.stepActive : done ? styles.stepDone : styles.stepPending}>
              <div style={styles.stepNumber}>{done ? "✓" : idx + 1}</div>
              <div style={styles.stepLabel}>{s.label.replace(/^\d+\.\s*/, "")}</div>
            </div>
            {idx < STAGES.length - 1 && (
              <div style={done ? styles.stepLineDone : styles.stepLine} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}


function FailedCard({ run, onReload }) {
  const reason = (run?.error_text || "").trim();
  return (
    <div style={styles.card}>
      <div style={{ fontSize: 18, fontWeight: 900, color: "#b91c1c" }}>
        Analysis run failed
      </div>
      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
        {reason
          ? "The pipeline stopped with the following error:"
          : "Something went wrong during a previous stage. Review the logs, fix the underlying issue, and start a new run when ready."}
      </div>
      {reason && (
        <pre
          style={{
            marginTop: 10,
            padding: "10px 12px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            color: "#991b1b",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {reason}
        </pre>
      )}
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <button onClick={onReload} style={styles.secondaryBtn}>Reload status</button>
      </div>
    </div>
  );
}


// -----------------------------------------------------------
// Stage 1 - Configure
// -----------------------------------------------------------

// DEPRECATED (LLM193): Stage 2 (AuditStage) now owns the running_audit UI.
// This function is kept temporarily but is no longer referenced by STAGES
// routing - it was the Stage 1 auto-advance panel (LLM163), superseded by
// the inline live progress card in AuditStage.
// eslint-disable-next-line no-unused-vars
function RunningAuditPanel({ run, brand, onReload, showToast }) {
  const [matrix, setMatrix] = useState(null);
  const [runStatuses, setRunStatuses] = useState({}); // rid -> { model, status, completed, total }
  const [advancing, setAdvancing] = useState(false);
  const advancedRef = React.useRef(false);

  useEffect(() => {
    if (!run.audit_id) return;
    let cancelled = false;
    const intervals = {};

    const refreshMatrix = async () => {
      try {
        const m = await fetchAuditMatrix(run.audit_id);
        if (!cancelled) setMatrix(m);
        return m;
      } catch {
        return null;
      }
    };

    (async () => {
      const m = await refreshMatrix();
      const cols = m?.matrix?.columns || [];
      const seed = {};
      for (const c of cols) {
        seed[c.run_id] = {
          model: c.model || "model",
          status: c.run_status || "running",
          completed: 0,
          total: 0,
        };
      }
      if (!cancelled) setRunStatuses(seed);

      for (const c of cols) {
        const rid = c.run_id;
        const initial = seed[rid];
        if (!initial) continue;
        if (
          initial.status === "done" ||
          initial.status === "error" ||
          initial.status === "cancelled"
        )
          continue;

        intervals[rid] = setInterval(async () => {
          try {
            const s = await fetchRunStatus(rid);
            if (cancelled) return;
            setRunStatuses((prev) => ({
              ...prev,
              [rid]: {
                ...(prev[rid] || {}),
                status: s.status || prev[rid]?.status,
                completed: s.completed ?? prev[rid]?.completed ?? 0,
                total: s.total ?? prev[rid]?.total ?? 0,
              },
            }));
            const terminal =
              s.status === "done" ||
              s.status === "error" ||
              s.status === "cancelled";
            if (terminal) {
              clearInterval(intervals[rid]);
              delete intervals[rid];
              await refreshMatrix();
            }
          } catch {
            // Transient errors swallowed; next tick retries.
          }
        }, 1200);
      }
    })();

    return () => {
      cancelled = true;
      Object.values(intervals).forEach(clearInterval);
    };
  }, [run.audit_id]);

  const statusesList = Object.entries(runStatuses);
  const allTerminal =
    statusesList.length > 0 &&
    statusesList.every(
      ([, info]) =>
        info.status === "done" ||
        info.status === "error" ||
        info.status === "cancelled"
    );
  const doneCount = statusesList.filter(([, i]) => i.status === "done").length;

  // Auto-advance to audit_complete once every run reaches terminal state.
  // advancedRef guards against re-triggering (state update -> re-render ->
  // effect runs again with allTerminal still true).
  //
  // Race: the backend ALSO transitions running_audit -> audit_complete
  // when the last per-model SRA run finishes (server-side hook in
  // sra_service._maybe_advance_to_audit_complete). With auto_advance on,
  // the chain continues into running_analysis before the frontend's POST
  // even lands, so the state machine rejects the duplicate transition.
  // We treat that case as success \u2014 the desired state has been reached
  // by another path. Only surface a real error when the run is still
  // somewhere upstream (running_audit, pending, etc.).
  useEffect(() => {
    if (!allTerminal || advancedRef.current || advancing) return;
    advancedRef.current = true;
    (async () => {
      setAdvancing(true);
      try {
        await analysisRunTransitionStatus(run.id, "audit_complete");
        showToast("Audit complete \u2014 review the matrix", "success");
        await onReload();
      } catch (e) {
        // The backend's auto-advance may have already moved the run past
        // audit_complete (into running_analysis / analysis_complete /
        // generating_pptx / ready). Refetch the live status and check;
        // closure-captured `run.status` is stale here.
        const ALREADY_PAST = new Set([
          "audit_complete",
          "running_analysis",
          "analysis_complete",
          "generating_pptx",
          "ready",
          "delivered",
        ]);
        let liveStatus = "";
        try {
          const fresh = await analysisRunGet(run.id);
          liveStatus = fresh?.status || "";
        } catch {
          /* ignore fetch error */
        }
        try {
          await onReload();
        } catch {
          /* ignore reload error */
        }
        if (ALREADY_PAST.has(liveStatus)) {
          // Backend got there first \u2014 silent no-op (no toast).
          return;
        }
        advancedRef.current = false;
        showToast(e?.message || "Could not advance to audit review", "error");
      } finally {
        setAdvancing(false);
      }
    })();
  }, [allTerminal, advancing, run.id, onReload, showToast]);

  return (
    <div className="audit-pane" style={{ marginTop: 0 }}>
      <div
        className="audit-card"
        style={{
          borderRadius: 18,
          border: "1px solid var(--border)",
          background: "#fff",
          boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
          overflow: "hidden",
          maxWidth: 1040,
          width: "100%",
        }}
      >
        <div
          className="audit-header"
          style={{
            padding: "14px 16px",
            background: "linear-gradient(120deg, rgba(0,0,0,0.94), rgba(17,24,39,0.90))",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 950 }}>
            Audit running \u2014{" "}
            <span style={{ color: "#ffcc33" }}>{brand.name}</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", fontWeight: 700 }}>
            {doneCount} / {statusesList.length} done
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {statusesList.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Loading audit matrix\u2026
            </div>
          ) : (
            <>
              <div className="run-status-wrap">
                <div
                  className="run-status-title"
                  style={{ fontWeight: 950, marginBottom: 8 }}
                >
                  Run progress
                </div>
                <ul className="run-status-list">
                  {statusesList.map(([rid, info]) => (
                    <li key={rid} className={`rs-item rs-${info.status}`}>
                      <span className="rs-model">{info.model || "model"}</span>
                      <span className="rs-dot" />
                      <span className="rs-state">
                        {info.status === "pending" && "Pending"}
                        {info.status === "running" &&
                          `Running\u2026 (${info.completed || 0} / ${info.total || "?"})`}
                        {info.status === "done" && "Done"}
                        {info.status === "cancelled" &&
                          `Cancelled (${info.completed || 0} / ${info.total || "?"})`}
                        {info.status === "error" &&
                          `Error: ${info.error || "Provider error"}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {advancing && (
                <div
                  style={{
                    marginTop: 14,
                    padding: 10,
                    background: "#fffbe8",
                    border: "1px solid #ffcc33",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#111827",
                    fontWeight: 700,
                  }}
                >
                  All runs complete \u2014 advancing to Stage 2 (Audit Review)\u2026
                </div>
              )}

              {allTerminal && !advancing && (
                <div
                  style={{
                    marginTop: 14,
                    padding: 10,
                    background: "#f0f9ff",
                    border: "1px solid #bae6fd",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#0c4a6e",
                  }}
                >
                  All runs reached a terminal state. Any second now the wizard
                  will advance to Stage 2 so you can review the matrix.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function ConfigureStage({ brand, run, setRunId, setRun, showToast, handle409, onReload, subBrands = [], focusSubBrandIds = [], setFocusSubBrandIds = () => {}, focusConfirmOpen = false, setFocusConfirmOpen = () => {}, focusConfirmedOnce = false, setFocusConfirmedOnce = () => {} }) {
  // ---- Templates + tiers (loaded once on mount) ----
  const [qTemplates, setQTemplates] = useState([]);
  const [mTemplates, setMTemplates] = useState([]);

  // ---- Brand-scoped data (DB-backed, re-fetched on change) ----
  // Pivot B: the wizard works on a RUN-SCOPED DRAFT of the question set,
  // not the brand's shared library. The draft lives client-side until the
  // user clicks Run Audit; at that point it's PUT to the run and snapshotted
  // into the audit. Library writes are a separate explicit action (Save to
  // brand library, gated by a blocker - Pivot C).
  //
  // `libraryQuestions` holds the brand's latest library for display + drift
  // compare. `draftQuestions` is null while the user hasn't diverged;
  // rendering then falls back to libraryQuestions. Once the user edits,
  // draftQuestions becomes the source of truth for this wizard session.
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [draftQuestions, setDraftQuestions] = useState(null);
  // Unsaved add/edit/remove changes (kind toggles autosave, so they don't set this).
  const [questionsDirty, setQuestionsDirty] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [qRefresh, setQRefresh] = useState(0);
  // The run we've already snapshotted the library into (guards against re-snapshotting
  // over the user's edits when the run object re-fetches with the same id).
  const snapshotRunRef = React.useRef(null);

  // The rendered list
  const brandQuestions = draftQuestions !== null ? draftQuestions : libraryQuestions;

  const hasDrifted =
    draftQuestions !== null &&
    (() => {
      const a = (draftQuestions || []).map((q) => (q.question_text || q.text || "").trim()).sort();
      const b = (libraryQuestions || []).map((q) => (q.question_text || q.text || "").trim()).sort();
      if (a.length !== b.length) return true;
      return a.some((t, i) => t !== b[i]);
    })();
  const [existingAliases, setExistingAliases] = useState([]);
  const [aliasRefresh, setAliasRefresh] = useState(0);

  // ---- Audit config fields ----
  const [title, setTitle] = useState("");
  const [titleAutoPreview, setTitleAutoPreview] = useState("");
  const titleUserOverriddenRef = React.useRef(false);
  const [promptTemplate, setPromptTemplate] = useState("{question_text}");
  const [modelTierId, setModelTierId] = useState(null);
  const [customModels, setCustomModels] = useState([]);
  const [useLocation, setUseLocation] = useState(!!brand?.country);
  const [country, setCountry] = useState(brand?.country || "");
  const [city, setCity] = useState("");
  // LLM196: opt in to end-to-end flow automation.
  const [autoAdvance, setAutoAdvance] = useState(false);

  // ---- Inline question composer + CSV ----
  const [newQuestionText, setNewQuestionText] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  // ---- SearchAPI (Google AI Overview) usage display ----
  const [serpUsage, setSerpUsage] = useState(null);
  const [serpUsageError, setSerpUsageError] = useState(null);
  const [serpUsageLoading, setSerpUsageLoading] = useState(false);

  // ---- Alias pre-flight ----
  const [aliasPreflightOpen, setAliasPreflightOpen] = useState(false);
  const [aliasProposals, setAliasProposals] = useState([]);
  const [aliasLoading, setAliasLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Load templates on mount
  useEffect(() => {
    (async () => {
      try {
        const [qt, mt] = await Promise.all([
          questionTemplatesList(),
          modelTierTemplatesList(),
        ]);
        setQTemplates(qt?.items || []);
        setMTemplates(mt?.items || []);
      } catch (e) {
        showToast(e?.message || "Failed to load templates", "error");
      }
    })();
  }, [showToast]);

  // Load the brand's library (the starting point + drift baseline). Bumped
  // by qRefresh when the library is overwritten from this wizard.
  useEffect(() => {
    if (!brand?.id) return;
    setLibraryLoaded(false);
    fetchQuestionsForBrand(brand.id)
      .then((r) => setLibraryQuestions(r?.items || r || []))
      .catch(() => setLibraryQuestions([]))
      .finally(() => setLibraryLoaded(true));
  }, [brand?.id, qRefresh]);

  // Stage-1 question source of truth. Two cases:
  //  • Run already has a draft → hydrate it (resume).
  //  • Run has no draft yet → SNAPSHOT the brand library into the run's draft on
  //    first render and persist it. This freezes the set so concurrent brand-hub
  //    edits can't leak in at audit-creation time, and it means draftQuestions is
  //    never null after load — so a kind toggle (or any edit) always lands in the
  //    persisted draft instead of silently falling back to the live library.
  useEffect(() => {
    if (!run?.id) return;
    if (run.draft_questions === undefined) return;
    // Load a run's questions exactly ONCE — a same-id run re-fetch (the wizard polls
    // run.status) must not clobber the user's unsaved client edits.
    if (snapshotRunRef.current === run.id) return;
    // kind MUST be carried through — leaving it off makes every re-loaded question
    // fall back to the UI's 'unbranded' default.
    if (run.draft_questions !== null) {
      snapshotRunRef.current = run.id;
      setDraftQuestions(
        (run.draft_questions || []).map((q, i) => ({
          id: q.question_id ?? `draft-${i}`,
          question_id: q.question_id ?? null,
          question_text: q.text,
          language: q.language || "en",
          kind: q.kind === "branded" ? "branded" : "unbranded",
        })),
      );
      return;
    }
    // draft is null → snapshot the library once (only while the run is still in
    // Stage 1). Deferred until the library has loaded (don't set the ref before then,
    // so the effect retries when it does).
    if (!libraryLoaded) return;
    if (run.status && run.status !== "pending") return;
    snapshotRunRef.current = run.id;
    const snap = (libraryQuestions || []).map((q) => ({
      id: q.id ?? q.question_id ?? genDraftId(),
      question_id: q.id ?? q.question_id ?? null,
      question_text: q.question_text || q.text || "",
      language: q.language || "en",
      kind: q.kind === "branded" ? "branded" : "unbranded",
    }));
    setDraftQuestions(snap);
    analysisRunSetDraftQuestions(
      run.id,
      snap.map((q) => ({
        text: q.question_text,
        question_id: q.question_id ?? null,
        language: q.language || "en",
        kind: q.kind === "branded" ? "branded" : "unbranded",
      })),
    ).catch(() => { /* best-effort freeze; Run-Audit re-persists anyway */ });
  }, [run?.id, run?.status, run?.draft_questions, libraryLoaded, libraryQuestions]);

  // Load brand aliases
  useEffect(() => {
    if (!brand?.id) return;
    fetchBrandAliases(brand.id)
      .then((r) => setExistingAliases((r?.items || []).map((a) => a.alias)))
      .catch(() => setExistingAliases([]));
  }, [brand?.id, aliasRefresh]);

  // Load SearchAPI usage (shown under the Google AI Overview group)
  useEffect(() => {
    setSerpUsageLoading(true);
    fetchSearchApiUsage()
      .then((data) => {
        setSerpUsage(data);
        setSerpUsageError(data?.error || null);
      })
      .catch((err) =>
        setSerpUsageError(err?.message || "Failed to load SearchAPI usage")
      )
      .finally(() => setSerpUsageLoading(false));
  }, []);

  // Fetch the auto-title preview once per brand. Pre-fills the Title input
  // with what the backend would use if left blank; user can overwrite or
  // clear it. We stop syncing once the user has typed into the field so
  // their edits aren't clobbered.
  useEffect(() => {
    if (!brand?.id) return;
    analysisRunPreviewTitle(brand.id)
      .then((resp) => {
        const autoTitle = resp?.title || "";
        setTitleAutoPreview(autoTitle);
        if (!titleUserOverriddenRef.current && !title) {
          setTitle(autoTitle);
        }
      })
      .catch(() => {
        // Silent - user can still type a title; backend will auto-generate
        // on submit if left blank.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id]);

  // ---- Question actions ----
  // All CRUD operates on the RUN-SCOPED DRAFT (client-side). Library is only
  // mutated via the explicit "Save to brand library" action (Pivot C) which
  // requires a blocker-clean state.
  //
  // On first edit we fork: draftQuestions goes from null -> a copy of
  // libraryQuestions -> the mutated draft. Null means "still the library".
  // MX2/MX4: kind = "unbranded" (generic discovery) | "branded" (brand-named).
  // The kind chosen for new typed questions, CSV uploads, and template applies.
  const [newQuestionKind, setNewQuestionKind] = useState("unbranded");

  const forkIfNeeded = () => {
    if (draftQuestions !== null) return draftQuestions;
    const forked = (libraryQuestions || []).map((q) => ({
      id: q.id ?? q.question_id ?? null,
      question_id: q.id ?? q.question_id ?? null,
      question_text: q.question_text || q.text || "",
      language: q.language || "en",
      kind: q.kind === "branded" ? "branded" : "unbranded",
    }));
    setDraftQuestions(forked);
    return forked;
  };

  const genDraftId = () => `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Client draft → server draft-question shape.
  const draftToServer = (arr) => (arr || []).map((q) => ({
    text: q.question_text,
    question_id: q.question_id ?? null,
    language: q.language || "en",
    kind: q.kind === "branded" ? "branded" : "unbranded",
  }));

  // Explicit Save for add/edit/remove — persists the run draft on a deliberate click
  // (kind toggles autosave separately). Requires the run to still be in Stage 1.
  const saveDraftQuestions = async () => {
    if (savingQuestions) return;
    if (!(run?.id && (run.status ?? "pending") === "pending")) {
      // No pending run yet — edits will be persisted when the audit is started.
      setQuestionsDirty(false);
      return;
    }
    setSavingQuestions(true);
    try {
      await analysisRunSetDraftQuestions(run.id, draftToServer(draftQuestions ?? libraryQuestions));
      setQuestionsDirty(false);
      showToast("Saved", "success");
    } catch (e) {
      showToast(e?.message || "Couldn't save questions - try again", "error");
    } finally {
      setSavingQuestions(false);
    }
  };

  const onAddQuestion = async () => {
    // Split on newlines so users can paste a list and add the whole batch
    // in one click. Single-line entries still work the same way.
    // Dedupes case-insensitively against existing draft questions to
    // avoid silently writing dupes (the backend will reject them later
    // anyway).
    const lines = (newQuestionText || "").split(/\r?\n/);
    const base = forkIfNeeded();
    const existingKeys = new Set(
      (base || [])
        .map((q) => (q.question_text || q.text || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const seen = new Set(existingKeys);
    const toAdd = [];
    for (const raw of lines) {
      const t = raw.trim().replace(/\s+/g, " ");
      if (!t) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      toAdd.push(t);
    }
    if (!toAdd.length) {
      // Either empty input or every line already in the draft. Either
      // way clear the box; no toast needed (Stage 1 is busy enough).
      setNewQuestionText("");
      return;
    }
    setDraftQuestions([
      ...base,
      ...toAdd.map((t) => ({
        id: genDraftId(),
        question_id: null,
        question_text: t,
        language: "en",
        kind: newQuestionKind,
      })),
    ]);
    setQuestionsDirty(true);
    setNewQuestionText("");
  };

  const onDeleteQuestion = async (qid) => {
    if (!window.confirm("Delete this question?")) return;
    const base = forkIfNeeded();
    setDraftQuestions(base.filter((q) => q.id !== qid));
    setQuestionsDirty(true);
  };

  const onEditQuestion = async (qid, currentText) => {
    const next = window.prompt("Edit question text:", currentText);
    if (next == null) return;
    const clean = next.trim();
    if (!clean || clean === currentText) return;
    const base = forkIfNeeded();
    setDraftQuestions(
      base.map((q) => (q.id === qid ? { ...q, question_text: clean } : q)),
    );
    setQuestionsDirty(true);
  };

  // Flip a single question's kind (unbranded ↔ branded) and SILENT-SAVE it. The type is
  // a durable per-question property, so we persist it to BOTH:
  //   1. the run draft, so THIS audit uses it (and flushes any pending add/edit/remove);
  //   2. the brand library (for library-backed questions), so the type sticks across
  //      future analyses instead of reverting to the snapshot.
  const onToggleQuestionKind = (qid) => {
    const base = forkIfNeeded();
    const target = base.find((q) => q.id === qid);
    if (!target) return;
    const nextKind = target.kind === "branded" ? "unbranded" : "branded";
    const next = base.map((q) => (q.id === qid ? { ...q, kind: nextKind } : q));
    setDraftQuestions(next);
    const jobs = [];
    if (run?.id && (run.status ?? "pending") === "pending") {
      jobs.push(analysisRunSetDraftQuestions(run.id, draftToServer(next)));
    }
    if (brand?.id && target.question_id) {
      jobs.push(updateQuestion(brand.id, target.question_id, undefined, nextKind));
    }
    (jobs.length ? Promise.all(jobs) : Promise.resolve())
      .then(() => { setQuestionsDirty(false); showToast("Saved", "success"); })
      .catch(() => showToast("Couldn't save the type change - try again", "error"));
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      // Parse the CSV client-side and append to the draft (first column is
      // the question text, matching the legacy server-side behaviour).
      const text = await file.text();
      const rows = text
        .split(/\r?\n/)
        .map((l) => l.split(",")[0].replace(/^"(.*)"$/, "$1").trim())
        .filter(Boolean);
      if (rows.length === 0) {
        showToast("CSV had no usable rows", "error");
      } else {
        const base = forkIfNeeded();
        const additions = rows.map((t) => ({
          id: genDraftId(), question_id: null, question_text: t, language: "en",
          kind: newQuestionKind,
        }));
        setDraftQuestions([...base, ...additions]);
        setQuestionsDirty(true);
        showToast(`Added ${rows.length} ${newQuestionKind} question${rows.length === 1 ? "" : "s"} from CSV`, "success");
      }
    } catch (err) {
      showToast(err?.message || "CSV upload failed", "error");
    } finally {
      setCsvUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onLoadTemplate = async (templateId, templateTitle) => {
    try {
      const t = await questionTemplateGet(templateId);
      const items = (t.items || [])
        .filter((i) => (i.text || "").trim())
        .map((i) => ({ text: i.text, kind: i.kind === "branded" ? "branded" : "unbranded" }));
      if (items.length === 0) {
        showToast(`Template "${templateTitle}" has no questions`, "error");
        return;
      }
      const base = forkIfNeeded();
      const additions = items.map((it) => ({
        id: genDraftId(), question_id: null, question_text: it.text, language: "en", kind: it.kind,
      }));
      setDraftQuestions([...base, ...additions]);
      setQuestionsDirty(true);
      showToast(
        `Loaded ${items.length} question${items.length === 1 ? "" : "s"} from "${templateTitle}"`,
        "success"
      );
    } catch (e) {
      showToast(e?.message || "Failed to load template", "error");
    }
  };

  const onRevertDraftToLibrary = () => {
    if (!hasDrifted) return;
    if (!window.confirm("Discard your edits and reload the brand's library questions?")) return;
    setDraftQuestions(null);
    setQuestionsDirty(false);
    // Persist the revert so it survives a resume (null → re-snapshots the live library).
    if (run?.id && (run.status ?? "pending") === "pending") {
      analysisRunSetDraftQuestions(run.id, null).catch(() => {});
    }
  };

  // ---- Library overwrite (Pivot C) ----
  const [overwriteBusy, setOverwriteBusy] = useState(false);
  const [overwriteBlocker, setOverwriteBlocker] = useState(null);
  // Shape: { conflicting_runs: [...] } when blocked, null otherwise

  const onOverwriteLibrary = async () => {
    if (!hasDrifted || !brand?.id) return;
    if (
      !window.confirm(
        "Replace the brand's library question set with your current edits?\n\n" +
          "This affects all future analyses that start from this brand. " +
          "Existing in-flight runs already past Stage 1 are unaffected."
      )
    ) {
      return;
    }
    setOverwriteBusy(true);
    setOverwriteBlocker(null);
    try {
      const payload = draftQuestions || [];
      await brandQuestionsOverwrite(brand.id, payload, run?.id ?? null);
      // Success: refresh the library view so drift collapses; keep the
      // run's draft in place (still the run's truth) - on next render
      // `hasDrifted` will be false because library now equals draft.
      setQRefresh((v) => v + 1);
      showToast("Brand library updated", "success");
    } catch (e) {
      const detail = e?.response?.detail || e?.detail;
      if (detail && detail.error === "library_overwrite_blocked") {
        setOverwriteBlocker(detail);
      } else {
        showToast(e?.message || "Overwrite failed", "error");
      }
    } finally {
      setOverwriteBusy(false);
    }
  };

  // ---- Resolved models ----
  const resolvedModels = useMemo(() => {
    if (modelTierId && modelTierId !== "custom") {
      return mTemplates.find((x) => x.id === modelTierId)?.models || [];
    }
    return customModels;
  }, [modelTierId, mTemplates, customModels]);

  // ---- Alias pre-flight ----
  const openAliasPreflight = async () => {
    let rid = run?.id;
    if (!rid) {
      try {
        const created = await analysisRunCreate({
          brand_id: brand.id,
          title: title || null,
          location_country: useLocation ? country : null,
          location_city: useLocation ? city : null,
          use_location: useLocation,
          model_overrides: resolvedModels,
          auto_advance: autoAdvance,
          focus_sub_brand_ids: focusSubBrandIds,
        });
        rid = created.id;
        setRunId(rid);
        setRun(created);
      } catch (e) {
        showToast(e?.message || "Could not create run", "error");
        return;
      }
    }
    setAliasLoading(true);
    try {
      const data = await analysisRunAliasPreflight(rid);
      setAliasProposals(
        (data.proposed_aliases || []).map((p) => ({ ...p, accepted: true }))
      );
      setAliasPreflightOpen(true);
    } catch (e) {
      showToast(e?.message || "Alias pre-flight failed", "error");
    } finally {
      setAliasLoading(false);
    }
  };

  const confirmAliases = async ({ saveToLibrary } = { saveToLibrary: false }) => {
    // Pivot D: accepted aliases land on the run-scoped aliases_pre_flight_json
    // by default - their scope is this specific analysis. Writing back to the
    // brand library is opt-in via the modal's checkbox; this stops one
    // analyst's alias choices from silently polluting everyone else's future
    // runs on the same brand.
    const accepted = aliasProposals.filter((p) => p.accepted).map((p) => p.alias);
    const rejected = aliasProposals.filter((p) => !p.accepted).map((p) => p.alias);

    try {
      // Always persist run-scoped capture (even when accepted=[] - auditable
      // record that pre-flight ran and what the user rejected).
      if (run?.id) {
        try {
          await analysisRunSetAliasesPreflight(run.id, {
            proposed: aliasProposals.map((p) => p.alias),
            accepted,
            rejected,
          });
        } catch {
          // Silent - non-fatal for the wizard; library write below is the
          // user-visible success path.
        }
      }

      if (saveToLibrary && accepted.length > 0) {
        const existingSet = new Set(existingAliases.map((a) => a.toLowerCase()));
        const toAdd = accepted.filter((a) => !existingSet.has(a.toLowerCase()));
        await Promise.all(toAdd.map((a) => addBrandAlias(brand.id, a)));
        setAliasRefresh((v) => v + 1);
        showToast(
          toAdd.length > 0
            ? `${toAdd.length} alias${toAdd.length === 1 ? "" : "es"} saved to ${brand.name} library`
            : "Aliases already in library",
          "success"
        );
      } else if (accepted.length > 0) {
        showToast(
          `${accepted.length} alias${accepted.length === 1 ? "" : "es"} captured for this run`,
          "success"
        );
      }
      setAliasPreflightOpen(false);
    } catch (e) {
      showToast(e?.message || "Failed to save aliases", "error");
    }
  };

  const removeAlias = async (alias) => {
    // Find alias id by name (existingAliases is just names; need to re-fetch with ids)
    try {
      const resp = await fetchBrandAliases(brand.id);
      const row = (resp?.items || []).find((a) => a.alias === alias);
      if (!row) return;
      const { deleteBrandAlias } = await import("../services/api");
      await deleteBrandAlias(brand.id, row.id);
      setAliasRefresh((v) => v + 1);
    } catch (e) {
      showToast(e?.message || "Remove failed", "error");
    }
  };

  // ---- Run audit ----
  const runAudit = async () => {
    if (!brandQuestions || brandQuestions.length === 0) {
      showToast("Add at least one question before starting the audit.", "error");
      return;
    }
    if (resolvedModels.length === 0) {
      showToast("Pick at least one model (via a tier or Custom).", "error");
      return;
    }
    if (useLocation && !country.trim()) {
      showToast("Country is required when location is enabled.", "error");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Ensure we have an analysis_run row
      let rid = run?.id;
      if (!rid) {
        const created = await analysisRunCreate({
          brand_id: brand.id,
          title: title || null,
          model_tier_template_id:
            modelTierId && modelTierId !== "custom" ? modelTierId : null,
          model_overrides: resolvedModels,
          location_country: useLocation ? country : null,
          location_city: useLocation ? city : null,
          use_location: useLocation,
          auto_advance: autoAdvance,
        });
        rid = created.id;
        setRunId(rid);
        setRun(created);
      }

      // 2a) Pivot B: persist the run-scoped draft before the audit is
      // materialised. When draftQuestions is null, the run will fall back
      // to the brand library at snapshot time - so we explicitly write null
      // for clarity on the row.
      try {
        await analysisRunSetDraftQuestions(
          rid,
          draftQuestions === null ? null : draftToServer(draftQuestions),
        );
        setQuestionsDirty(false);
      } catch (e) {
        showToast(e?.message || "Failed to save draft questions", "error");
        setSubmitting(false);
        return;
      }

      // 2b) Create audit. analysis_run_id lets the backend snapshot from
      // the run's draft (falling back to the brand library if draft is null).
      const auditResp = await createAudit({
        brand_id: brand.id,
        models: resolvedModels,
        title: title || null,
        prompt_template: promptTemplate || "{question_text}",
        use_location: useLocation,
        location_country: useLocation ? country : null,
        location_city: useLocation ? city : null,
        analysis_run_id: rid,
      });
      const auditId = auditResp?.audit_id;
      const runIds = auditResp?.run_ids || [];
      if (!auditId) throw new Error("Audit creation did not return an id");

      // 3) Link audit to the analysis run
      await analysisRunLinkAudit(rid, auditId);

      // 4) Advance status to running_audit BEFORE kicking runs. startRun
      //    blocks until the run completes, so awaiting it would delay the
      //    transition and leave the user staring at a silent Stage 1 for
      //    minutes. Transition first, fire-and-forget the runs, and let
      //    Stage 2's polling surface progress immediately.
      await analysisRunTransitionStatus(rid, "running_audit");

      // Fire-and-forget. Transient provider errors (OpenAI RequestError,
      // rate limits, etc.) show up in per-run status via fetchRunStatus;
      // the UI surfaces them in the run-progress list so the user can retry
      // individual runs from the matrix without blocking the wizard.
      runIds.forEach((id) => {
        startRun(id).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn(`Run ${id} failed:`, err);
        });
      });

      showToast("Audit started", "success");
      const fresh = await analysisRunGet(rid);
      setRun(fresh);
    } catch (e) {
      if (!handle409(e)) {
        showToast(e?.message || "Could not start audit", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const questionCount = brandQuestions.length;

  const canRun = !submitting && resolvedModels.length > 0 && questionCount > 0;

  // running_audit is now Stage 2's responsibility - when the backend
  // transitions, the wizard re-routes automatically. We intentionally do
  // NOT early-return here anymore.

  return (
    <div className="audit-pane" style={{ marginTop: 0 }}>
      <div
        className="audit-card"
        style={{
          borderRadius: 18,
          border: "1px solid var(--border)",
          background: "#fff",
          boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
          overflow: "hidden",
          // Override theme.css's 720px cap for audit-pane > audit-card:first-of-type
          // so the wizard has room for the richer Stage 1 (templates, questions,
          // aliases, full audit config) without feeling cramped.
          maxWidth: 1040,
          width: "100%",
        }}
      >
        <div
          className="audit-header"
          style={{
            padding: "14px 16px",
            background: "linear-gradient(120deg, rgba(0,0,0,0.94), rgba(17,24,39,0.90))",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 950 }}>
            Start a new Audit -{" "}
            <span style={{ color: "#ffcc33", fontWeight: 950 }}>{brand.name}</span>
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", fontWeight: 700 }}>
            {questionCount} question{questionCount === 1 ? "" : "s"}
          </div>
        </div>

        <div className="grid-form" style={{ padding: 16 }}>
          {/* Templates (new) */}
          <label className="lbl">Templates</label>
          <div>
            <div className="model-grid">
              {qTemplates.length === 0 && (
                <small className="hint">
                  No templates yet. <a href="/templates">Create one</a> or add questions manually below.
                </small>
              )}
              {qTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onLoadTemplate(t.id, t.title)}
                  className="model-chip"
                  title={`Append questions from "${t.title}" to this brand`}
                  style={{ flexDirection: "column", alignItems: "flex-start", gap: 2 }}
                >
                  <span style={{ fontWeight: 800 }}>{t.title}</span>
                  <span style={{ fontSize: 10, color: "#6b7280" }}>{t.industry}</span>
                </button>
              ))}
              <a
                href="/templates"
                style={{
                  alignSelf: "center",
                  fontSize: 11,
                  color: "#6b7280",
                  textDecoration: "underline",
                  marginLeft: 6,
                }}
              >
                Manage templates →
              </a>
            </div>
          </div>

          {/* Questions (CRUD + CSV) */}
          <label className="lbl">Questions ({questionCount})</label>
          <div>
            {/* MX4: organic vs prompted picker. New questions / CSV / template
                applies all inherit the chosen kind. Per-row badge lets users
                flip individual entries. */}
            <div
              style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                marginBottom: 8,
                fontSize: 11,
                color: "#6b7280",
              }}
            >
              <span style={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Add as:
              </span>
              {[
                { k: "unbranded", label: "Organic", help: "Generic discovery (e.g. 'best digital bank in Romania')" },
                { k: "branded", label: "Prompted", help: "Brand-named visibility (e.g. 'how is BRD perceived?')" },
              ].map((o) => {
                const active = newQuestionKind === o.k;
                return (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setNewQuestionKind(o.k)}
                    title={o.help}
                    style={{
                      padding: "3px 10px",
                      borderRadius: 999,
                      border: active
                        ? `1px solid ${o.k === "branded" ? "#bfdbfe" : "#9ca3af"}`
                        : "1px solid #e5e7eb",
                      background: active ? (o.k === "branded" ? "#eff6ff" : "#f3f4f6") : "#fff",
                      color: active ? (o.k === "branded" ? "#1e3a8a" : "#111827") : "#6b7280",
                      fontSize: 10,
                      fontWeight: 800,
                      cursor: "pointer",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
              <textarea
                className="fld"
                value={newQuestionText}
                onChange={(e) => setNewQuestionText(e.target.value)}
                onKeyDown={(e) => {
                  // Ctrl/Cmd+Enter submits; Enter alone adds a new line so
                  // users can paste multi-line lists without surprises.
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    onAddQuestion();
                  }
                }}
                placeholder="Type or paste questions, one per line… (Ctrl+Enter to add)"
                rows={3}
                style={{ flex: 1, resize: "vertical", lineHeight: 1.5, fontFamily: "inherit" }}
              />
              <button
                type="button"
                onClick={onAddQuestion}
                disabled={!newQuestionText.trim()}
                className="btn-secondary"
              >
                + Add
              </button>
              <label
                className="btn-secondary"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: csvUploading ? "wait" : "pointer",
                  opacity: csvUploading ? 0.6 : 1,
                  margin: 0,
                }}
                title="Import questions from a CSV (first column)"
              >
                {csvUploading ? "Uploading…" : "Import CSV"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onCsvUpload}
                  disabled={csvUploading}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {questionsDirty && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
                padding: "8px 12px", borderRadius: 8, background: "#fffbeb",
                border: "1px solid #fde68a" }}>
                <span style={{ fontSize: 13, color: "#92400e", flex: 1 }}>
                  You have unsaved question changes.
                </span>
                <button type="button" className="btn-primary" onClick={saveDraftQuestions}
                  disabled={savingQuestions} style={{ padding: "6px 14px" }}>
                  {savingQuestions ? "Saving…" : "Save questions"}
                </button>
              </div>
            )}

            {hasDrifted && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  marginBottom: 8,
                  borderRadius: 10,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1e3a8a",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <span>
                  Your questions differ from the brand library. This run will use these;
                  the library is unchanged.
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    onClick={onRevertDraftToLibrary}
                    className="btn-secondary"
                    style={{ padding: "4px 10px", fontSize: 11, borderRadius: 8 }}
                    title="Discard edits and reload the library as-is"
                  >
                    Revert
                  </button>
                  <button
                    type="button"
                    onClick={onOverwriteLibrary}
                    disabled={overwriteBusy}
                    className="btn-primary"
                    style={{ padding: "4px 10px", fontSize: 11, borderRadius: 8, fontWeight: 800 }}
                    title="Replace the brand library with your current set"
                  >
                    {overwriteBusy ? "Saving…" : "Save to brand library"}
                  </button>
                </div>
              </div>
            )}
            {questionCount === 0 ? (
              <small className="hint">
                No questions yet. Load a template, type one above, or import a CSV.
              </small>
            ) : (
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  maxHeight: 260,
                  overflowY: "auto",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  padding: 6,
                  background: "#fafafa",
                }}
              >
                {brandQuestions.map((q, i) => (
                  <li
                    key={q.id || i}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "8px 10px",
                      background: "#fff",
                      border: "1px solid #f1f5f9",
                      borderRadius: 8,
                      alignItems: "center",
                      fontSize: 13,
                      color: "#111827",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: "#6b7280",
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        padding: "2px 8px",
                        minWidth: 34,
                        textAlign: "center",
                      }}
                    >
                      Q{i + 1}
                    </span>
                    {/* MX4: clickable kind badge - flips between organic and prompted */}
                    {(() => {
                      const isPrompted = (q.kind || "unbranded") === "branded";
                      return (
                        <button
                          type="button"
                          onClick={() => onToggleQuestionKind(q.id)}
                          title={
                            isPrompted
                              ? "Prompted (brand-named). Click to flip to organic."
                              : "Organic (generic). Click to flip to prompted."
                          }
                          style={{
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontSize: 9,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            cursor: "pointer",
                            border: isPrompted ? "1px solid #bfdbfe" : "1px solid #d1d5db",
                            background: isPrompted ? "#eff6ff" : "#f9fafb",
                            color: isPrompted ? "#1e3a8a" : "#374151",
                          }}
                        >
                          {isPrompted ? "Prompted" : "Organic"}
                        </button>
                      );
                    })()}
                    <span style={{ flex: 1, lineHeight: 1.4 }}>
                      {q.text || q.question_text || ""}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onEditQuestion(q.id, q.text || q.question_text || "")
                      }
                      title="Edit"
                      style={styles.iconBtn}
                    >
                      <EditIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteQuestion(q.id)}
                      title="Remove"
                      style={styles.iconBtnDanger}
                    >
                      <TrashIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Title */}
          <label className="lbl">Title</label>
          <input
            className="fld"
            type="text"
            value={title}
            onChange={(e) => {
              titleUserOverriddenRef.current = true;
              setTitle(e.target.value);
            }}
            placeholder={titleAutoPreview || "Leave blank for an auto-generated title"}
          />
          {titleAutoPreview && (
            <small className="hint">
              Leave blank to use the auto-title:{" "}
              <code>{titleAutoPreview}</code>. Admins can change the template in
              settings (<code>analysis_run.auto_title_template</code>).
            </small>
          )}

          {/* Models */}
          <label className="lbl">Models</label>
          <div className="model-box">
            <div className="model-grid">
              {mTemplates.map((t) => (
                <label
                  key={t.id}
                  className={`model-chip ${modelTierId === t.id ? "on" : ""}`}
                  style={{ flexDirection: "column", alignItems: "flex-start", gap: 2 }}
                >
                  <input
                    type="radio"
                    name="tier"
                    checked={modelTierId === t.id}
                    onChange={() => setModelTierId(t.id)}
                    style={{ marginRight: 4 }}
                  />
                  <span style={{ fontWeight: 800 }}>{t.title}</span>
                  <span style={{ fontSize: 10, color: "#6b7280" }}>
                    {t.models?.length || 0} model{t.models?.length === 1 ? "" : "s"}
                  </span>
                </label>
              ))}
              <label className={`model-chip ${modelTierId === "custom" ? "on" : ""}`}>
                <input
                  type="radio"
                  name="tier"
                  checked={modelTierId === "custom"}
                  onChange={() => setModelTierId("custom")}
                />
                <span style={{ fontWeight: 800 }}>Custom</span>
              </label>
            </div>

            {modelTierId === "custom" && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                {MODEL_GROUPS.map((group) => (
                  <div key={group.provider} className="model-group" style={{ borderRadius: 14 }}>
                    <div
                      className="model-group-header"
                      style={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        marginBottom: 6,
                      }}
                    >
                      {group.provider}
                    </div>
                    <div className="model-grid">
                      {group.models.map((m) => (
                        <label
                          key={m.id}
                          className={`model-chip ${customModels.includes(m.id) ? "on" : ""}`}
                          style={{
                            borderRadius: 999,
                            boxShadow: customModels.includes(m.id)
                              ? "0 10px 22px rgba(255,204,51,0.18)"
                              : "none",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={customModels.includes(m.id)}
                            onChange={() => {
                              const set = new Set(customModels);
                              if (set.has(m.id)) set.delete(m.id);
                              else set.add(m.id);
                              setCustomModels(Array.from(set));
                            }}
                          />
                          <span>{m.label}</span>
                        </label>
                      ))}
                    </div>

                    {group.provider === "Google AI Overview" && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#4b5563" }}>
                        {serpUsageLoading && <span>Loading SearchAPI usage…</span>}
                        {!serpUsageLoading && serpUsageError && (
                          <span>
                            SearchAPI: <strong>not configured</strong> ({serpUsageError})
                          </span>
                        )}
                        {!serpUsageLoading && serpUsage && serpUsage.enabled && !serpUsageError && (
                          <span>
                            SearchAPI credits remaining:{" "}
                            <strong>{serpUsage.remaining_credits ?? "?"}</strong>{" "}/{" "}
                            {serpUsage.monthly_allowance ?? "?"} this month
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {resolvedModels.length > 0 && (
              <small className="hint">
                <b>{resolvedModels.length} selected:</b> {resolvedModels.join(", ")}
              </small>
            )}
          </div>

          {/* Prompt template */}
          <label className="lbl">Prompt Template</label>
          <textarea
            className="fld"
            rows={3}
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            placeholder="{question_text}"
          />

          {/* Location */}
          <label className="lbl">Location</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800 }}>
              <input
                type="checkbox"
                checked={useLocation}
                onChange={(e) => setUseLocation(e.target.checked)}
              />
              <span>Use location</span>
            </label>

            {useLocation && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  alignItems: "start",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "#374151" }}>
                    Country <span style={{ color: "#b91c1c" }}>*</span>
                  </div>
                  <input
                    className="fld"
                    placeholder="e.g. Romania"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "#374151" }}>
                    City <span style={{ color: "#6b7280" }}>(optional)</span>
                  </div>
                  <input
                    className="fld"
                    placeholder="e.g. Bucharest"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* FB006 - Audited brand selector. Shown only when the
              current brand is a master with declared sub-brands. */}
          {subBrands.length > 0 && (
            <>
              <label className="lbl">Audited brand</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <small className="hint" style={{ fontSize: 12, color: "#6b7280" }}>
                  Pick the sub-brand(s) the audit prompts will target. Leave
                  empty to audit <b>{brand?.name || "the master brand"}</b>{" "}
                  itself. The Brand Universe in the report always combines
                  the master and every sub-brand, no matter what you pick here.
                </small>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {subBrands.map((sb) => {
                    const sid = Number(sb.id);
                    const on = focusSubBrandIds.includes(sid);
                    return (
                      <button
                        key={sid}
                        type="button"
                        className={`model-chip ${on ? "on" : ""}`}
                        onClick={() => {
                          setFocusSubBrandIds((prev) => {
                            const next = on
                              ? prev.filter((x) => x !== sid)
                              : [...prev, sid];
                            // Trigger the explainer dialog the first time
                            // the user picks any sub-brand on this page load.
                            if (!on && !focusConfirmedOnce) {
                              setFocusConfirmOpen(true);
                            }
                            return next;
                          });
                        }}
                        title={
                          on
                            ? `Selected as audited brand - click to remove`
                            : `Add ${sb.name} as the audited brand`
                        }
                      >
                        {sb.name}
                      </button>
                    );
                  })}
                </div>
                {focusSubBrandIds.length > 0 && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#1e3a8a",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 8,
                      padding: "8px 10px",
                    }}
                  >
                    The audit will run against{" "}
                    <b>
                      {subBrands
                        .filter((sb) => focusSubBrandIds.includes(Number(sb.id)))
                        .map((sb) => sb.name)
                        .join(", ")}
                    </b>
                    . The Brand Universe table will still aggregate{" "}
                    {brand?.name || "the master"} + all {subBrands.length}{" "}
                    sub-brands.
                  </div>
                )}
              </div>
            </>
          )}

          {/* Aliases */}
          <label className="lbl">Brand Aliases</label>
          <div>
            {existingAliases.length === 0 ? (
              <small className="hint">
                No aliases yet. Run pre-flight to let the system propose name variants.
              </small>
            ) : (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {existingAliases.map((alias) => (
                  <span
                    key={alias}
                    className="model-chip on"
                    style={{ cursor: "default", gap: 8 }}
                  >
                    {alias}
                    <button
                      type="button"
                      onClick={() => removeAlias(alias)}
                      title="Remove alias"
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 999,
                        border: "none",
                        background: "transparent",
                        color: "#6b7280",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={openAliasPreflight}
              disabled={aliasLoading}
              className="btn-secondary"
            >
              {aliasLoading ? "Running…" : "Run alias pre-flight"}
            </button>
          </div>
        </div>

        <div className="actions" style={{ padding: "0 16px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "#374151" }}>
            <b>{questionCount}</b> question{questionCount === 1 ? "" : "s"} ×{" "}
            <b>{resolvedModels.length}</b> model{resolvedModels.length === 1 ? "" : "s"}
          </div>
          {/* LLM196: opt in to end-to-end flow automation. */}
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 10,
              border: autoAdvance ? "1px solid #2563eb" : "1px solid #e5e7eb",
              background: autoAdvance ? "#eff6ff" : "#fff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 700,
              color: autoAdvance ? "#1e3a8a" : "#374151",
              userSelect: "none",
            }}
            title="When on, the wizard runs audit → analysis → PPTX back-to-back and notifies you when done. Mark-delivered stays manual."
          >
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
              style={{ margin: 0 }}
            />
            Run end-to-end and notify me when done
          </label>
          <button
            type="button"
            onClick={runAudit}
            disabled={!canRun}
            title={
              submitting
                ? "Starting…"
                : questionCount === 0
                  ? "Add at least one question before starting the audit."
                  : resolvedModels.length === 0
                    ? "Pick at least one model (a tier or Custom) before starting the audit."
                    : "Start the audit"
            }
            className="btn-primary"
            style={{ padding: "12px 18px", borderRadius: 14, fontWeight: 900 }}
          >
            {submitting ? "Starting…" : "Run Audit →"}
          </button>
        </div>
        {!submitting && (questionCount === 0 || resolvedModels.length === 0) && (
          <div
            style={{
              padding: "0 16px 16px",
              fontSize: 12,
              color: "#b45309",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span aria-hidden="true">⚠</span>
            {questionCount === 0
              ? "Add at least one question before you can start the audit."
              : "Pick at least one model (a tier or Custom) before you can start the audit."}
          </div>
        )}
      </div>

      {aliasPreflightOpen && (
        <AliasPreflightModal
          proposals={aliasProposals}
          setProposals={setAliasProposals}
          onClose={() => setAliasPreflightOpen(false)}
          onConfirm={confirmAliases}
        />
      )}

      {/* FB006 - explainer dialog the first time a user picks a sub-brand.
          Clarifies the difference between "audited brand" and "Brand Universe"
          so the choice doesn't silently change interpretation of the report. */}
      {focusConfirmOpen && (
        <FocusBrandConfirmModal
          masterName={brand?.name || "the master brand"}
          subBrandCount={subBrands.length}
          selectedNames={subBrands
            .filter((sb) => focusSubBrandIds.includes(Number(sb.id)))
            .map((sb) => sb.name)}
          onCancel={() => {
            // Revert the addition that triggered this dialog.
            setFocusSubBrandIds([]);
            setFocusConfirmOpen(false);
          }}
          onConfirm={() => {
            setFocusConfirmedOnce(true);
            setFocusConfirmOpen(false);
          }}
        />
      )}

      {overwriteBlocker && (
        <LibraryOverwriteBlockerModal
          conflicting={overwriteBlocker.conflicting_runs || []}
          onClose={() => setOverwriteBlocker(null)}
        />
      )}
    </div>
  );
}


/**
 * FB006 - Confirms the user understands "audited brand" vs "Brand Universe".
 * Triggered the first time a sub-brand is selected in Stage 1 of the wizard.
 */
function FocusBrandConfirmModal({ masterName, subBrandCount, selectedNames, onCancel, onConfirm }) {
  const selectedLabel =
    selectedNames.length === 0
      ? "the chosen sub-brand"
      : selectedNames.join(", ");
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "#fff", borderRadius: 12, padding: 24, maxWidth: 560,
          width: "92vw", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
          Confirm the audited brand
        </h3>
        <p style={{ margin: "0 0 12px", fontSize: 14, color: "#334155", lineHeight: 1.5 }}>
          You're about to audit <b>{selectedLabel}</b>. Audit questions will be
          run with this name (or these names) as the subject.
        </p>
        <p style={{ margin: "0 0 12px", fontSize: 14, color: "#334155", lineHeight: 1.5 }}>
          The <b>Brand Universe</b> table in the report will still cover{" "}
          <b>{masterName}</b> together with all {subBrandCount} declared
          sub-brand{subBrandCount === 1 ? "" : "s"} - regardless of which
          sub-brand you focus on. The universe view measures how the entire
          brand family is perceived; the audited brand measures the specific
          slice you're investigating today.
        </p>
        <p style={{ margin: "0 0 18px", fontSize: 13, color: "#6b7280" }}>
          Leave the audited brand empty if you want the master brand itself
          to be the subject of the audit.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm}>
            Got it - continue
          </button>
        </div>
      </div>
    </div>
  );
}


function LibraryOverwriteBlockerModal({ conflicting, onClose }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 2000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: 16, maxWidth: 560, width: "100%",
          padding: 22, boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827", marginBottom: 6 }}>
          Can't overwrite yet
        </div>
        <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, marginBottom: 14 }}>
          {conflicting.length} other analysis run{conflicting.length === 1 ? "" : "s"} for
          this brand {conflicting.length === 1 ? "is" : "are"} still in Stage 1 and would be
          affected. Each needs to either click <strong>Run Audit</strong> (which freezes
          their questions into that run) or abort, before you can overwrite the library.
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px 0", display: "flex", flexDirection: "column", gap: 6 }}>
          {conflicting.map((r) => (
            <li
              key={r.id}
              style={{
                padding: "8px 12px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 12,
                color: "#111827",
              }}
            >
              <strong>Run #{r.id}</strong>
              {r.title ? <> - {r.title}</> : null}
              {r.assignee_user_id ? (
                <span style={{ color: "#6b7280", marginLeft: 8 }}>
                  · assignee user {r.assignee_user_id}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            className="btn-primary"
            style={{ padding: "8px 16px", borderRadius: 10, fontWeight: 800 }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}


function AliasPreflightModal({ proposals, setProposals, onClose, onConfirm }) {
  const [saveToLibrary, setSaveToLibrary] = React.useState(false);
  const toggle = (i) =>
    setProposals((prev) =>
      prev.map((p, idx) => (idx === i ? { ...p, accepted: !p.accepted } : p))
    );
  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Alias pre-flight proposals</h3>
        <p style={styles.hint}>
          Accept the ones that are real name variants for this brand. Accepted
          aliases are captured for <strong>this analysis run only</strong>.
        </p>
        {proposals.length === 0 ? (
          <div style={styles.empty}>
            No proposals returned. You can still run the audit - aliases can be
            added from the Brands Hub edit form.
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {proposals.map((p, i) => (
              <label
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: 10,
                  borderBottom: "1px solid #f3f4f6",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!p.accepted}
                  onChange={() => toggle(i)}
                />
                <div>
                  <div style={{ fontWeight: 800 }}>{p.alias}</div>
                  {p.reason && (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{p.reason}</div>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
            padding: "8px 10px",
            borderRadius: 8,
            background: saveToLibrary ? "#eff6ff" : "#f9fafb",
            border: saveToLibrary ? "1px solid #bfdbfe" : "1px solid #e5e7eb",
            fontSize: 12,
            color: saveToLibrary ? "#1e3a8a" : "#374151",
            fontWeight: 600,
            cursor: "pointer",
          }}
          title="Leave unchecked to keep these aliases scoped to this run only. Check to add them to the brand's shared library for all future analyses."
        >
          <input
            type="checkbox"
            checked={saveToLibrary}
            onChange={(e) => setSaveToLibrary(e.target.checked)}
          />
          Also save accepted aliases to the brand library
        </label>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={styles.secondaryBtn}>Cancel</button>
          <button
            onClick={() => onConfirm({ saveToLibrary })}
            style={styles.primaryBtn}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------
// Stage 2 - Audit Review
// -----------------------------------------------------------

// Collapsed summary of stages completed before the current one. Rendered at
// the top of Stages 2, 3, and 4 so the user can always look back at their
// setup / matrix / analysis without losing the current stage context.
function PreviousStagesSummary({ run, brand, currentStageKey }) {
  const stageOrder = ["configure", "audit", "analysis", "deliverable"];
  const curIdx = stageOrder.indexOf(currentStageKey);
  if (curIdx <= 0) return null;

  // Only render cards for stages strictly before the current one
  const priorKeys = stageOrder.slice(0, curIdx);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
      {priorKeys.map((key) => (
        <PriorStageCard key={key} stageKey={key} run={run} brand={brand} />
      ))}
    </div>
  );
}


function PriorStageCard({ stageKey, run, brand }) {
  const [open, setOpen] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // Lazy-load matrix only when the Audit summary card is expanded
  useEffect(() => {
    if (stageKey !== "audit") return;
    if (!open || matrix || matrixLoading) return;
    if (!run.audit_id) return;
    setMatrixLoading(true);
    fetchAuditMatrix(run.audit_id)
      .then((m) => setMatrix(m))
      .catch(() => setMatrix(null))
      .finally(() => setMatrixLoading(false));
  }, [open, stageKey, run.audit_id, matrix, matrixLoading]);

  const labels = {
    configure: { title: "Stage 1 - Analysis Setup", icon: "⚙" },
    audit: { title: "Stage 2 - Answer matrix", icon: "▦" },
    analysis: { title: "Stage 3 - Analysis", icon: "☰" },
  };
  const meta = labels[stageKey];

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#374151",
              fontWeight: 900,
            }}
          >
            {meta.icon}
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
            {meta.title}
          </span>
          <PriorStageHint stageKey={stageKey} run={run} />
        </div>
        <span
          style={{
            fontSize: 11,
            color: "#6b7280",
            fontWeight: 700,
          }}
        >
          {open ? "Collapse ▴" : "Expand ▾"}
        </span>
      </button>

      {open && (
        <div style={{ padding: "4px 14px 16px", borderTop: "1px solid #f3f4f6" }}>
          {stageKey === "configure" && <ConfigureSummary run={run} brand={brand} />}
          {stageKey === "audit" && (
            <AuditSummary
              matrix={matrix}
              loading={matrixLoading}
              onRefresh={async () => {
                const m = await fetchAuditMatrix(run.audit_id);
                setMatrix(m);
              }}
            />
          )}
          {stageKey === "analysis" && <AnalysisSummary run={run} />}
        </div>
      )}
    </div>
  );
}


// One-line preview that stays visible even when the card is collapsed.
function PriorStageHint({ stageKey, run }) {
  if (stageKey === "configure") {
    const parts = [];
    if (run.model_overrides?.length) {
      parts.push(`${run.model_overrides.length} models`);
    }
    if (run.location_country) parts.push(run.location_country);
    return (
      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}>
        {parts.join(" · ") || "audit configuration"}
      </span>
    );
  }
  if (stageKey === "audit") {
    return (
      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}>
        raw answer matrix
      </span>
    );
  }
  if (stageKey === "analysis") {
    return (
      <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4 }}>
        brand analysis report
      </span>
    );
  }
  return null;
}


function ConfigureSummary({ run, brand }) {
  const models = run.model_overrides || [];
  const fields = [
    { label: "Brand", value: brand?.name || "-" },
    { label: "Country", value: brand?.country || "-" },
    { label: "Industry", value: brand?.industry || "-" },
    { label: "Title", value: run.title || "(untitled)" },
    {
      label: "Location override",
      value: run.use_location
        ? `${run.location_country || ""}${run.location_city ? " · " + run.location_city : ""}`
        : "off",
    },
    {
      label: "Estimated cost",
      value:
        run.estimated_cost_usd != null
          ? `$${Number(run.estimated_cost_usd).toFixed(3)}`
          : run.actual_cost_usd != null
            ? `$${Number(run.actual_cost_usd).toFixed(3)} (actual)`
            : "-",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 10 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 10,
        }}
      >
        {fields.map((f) => (
          <div key={f.label}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 900,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 3,
              }}
            >
              {f.label}
            </div>
            <div style={{ fontSize: 13, color: "#111827", fontWeight: 600 }}>
              {f.value}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            marginBottom: 6,
          }}
        >
          Models ({models.length})
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {models.length === 0 && (
            <span style={{ fontSize: 12, color: "#9ca3af" }}>(none recorded)</span>
          )}
          {models.map((m) => (
            <span
              key={m}
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                background: "#fffbe8",
                border: "1px solid #ffcc33",
                fontSize: 11,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


function AuditSummary({ matrix, loading, onRefresh }) {
  if (loading) {
    return (
      <div style={{ padding: "14px 0", fontSize: 12, color: "#6b7280" }}>
        Loading matrix…
      </div>
    );
  }
  if (!matrix || !(matrix?.matrix?.rows || []).length) {
    return (
      <div style={{ padding: "14px 0", fontSize: 12, color: "#6b7280" }}>
        No matrix data available.
      </div>
    );
  }
  return (
    <div style={{ paddingTop: 10 }}>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
        {(matrix?.matrix?.rows || []).length} questions ×{" "}
        {(matrix?.matrix?.columns || []).length} models
      </div>
      <AuditMatrix
        audit={matrix?.audit}
        matrix={matrix?.matrix}
        onRefresh={onRefresh}
      />
    </div>
  );
}


function AnalysisSummary({ run }) {
  if (!run.copilot_session_id) {
    return (
      <div style={{ padding: "14px 0", fontSize: 12, color: "#6b7280" }}>
        No analysis session linked.
      </div>
    );
  }
  return (
    <div style={{ paddingTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      {run.validator_status && <ValidatorBadge status={run.validator_status} />}
      <a
        href={`/analysis/${run.copilot_session_id}`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "10px 16px",
          borderRadius: 12,
          background: "#111827",
          color: "#ffcc33",
          fontWeight: 800,
          fontSize: 12,
          textDecoration: "none",
          border: "1px solid #ffcc33",
          alignSelf: "flex-start",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3h7v7" />
          <path d="M10 14L21 3" />
          <path d="M21 14v7h-7" />
          <path d="M3 10V3h7" />
        </svg>
        See analysis report
      </a>
    </div>
  );
}


// LLM187: per-row "what's running right now?" subtext + expandable
// per-question breakdown. Uses the items / in_flight / last_completed
// fields surfaced by GET /audits/run/{id}/status.
function RunStatusRow({ rid, info }) {
  const [expanded, setExpanded] = useState(false);
  const items = info.items || [];
  const lastCompleted = info.last_completed;
  const inFlightCount = (info.in_flight || []).length;

  // Trim long question text for the inline subtext
  const truncate = (s, n = 80) => {
    if (!s) return "";
    return s.length > n ? `${s.slice(0, n - 1)}…` : s;
  };

  const stateText = (() => {
    if (info.status === "pending") return "Pending";
    if (info.status === "done") return "Done";
    if (info.status === "cancelled")
      return `Cancelled (${info.completed || 0} / ${info.total || "?"})`;
    if (info.status === "error") return `Error: ${info.error || "Provider error"}`;
    return `Running… (${info.completed || 0} / ${info.total || "?"})`;
  })();

  const subtext = (() => {
    if (info.status !== "running") return null;
    if (lastCompleted?.text) {
      return `Last: Q${lastCompleted.question_id ?? "?"} · ${truncate(lastCompleted.text)}`;
    }
    if (inFlightCount > 0) {
      return `${inFlightCount} question${inFlightCount === 1 ? "" : "s"} in flight`;
    }
    return null;
  })();

  return (
    <li
      className={`rs-item rs-${info.status}`}
      style={{ flexDirection: "column", alignItems: "stretch" }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: items.length > 0 ? "pointer" : "default" }}
        onClick={() => items.length > 0 && setExpanded((v) => !v)}
        title={items.length > 0 ? "Click for per-question breakdown" : undefined}
      >
        <span className="rs-model">{info.model || "model"}</span>
        <span className="rs-dot" />
        <span className="rs-state">{stateText}</span>
        {items.length > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}>
            {expanded ? "▲" : "▼"}
          </span>
        )}
      </div>
      {subtext && (
        <div
          style={{
            fontSize: 11,
            color: "#6b7280",
            marginTop: 4,
            marginLeft: 16,
            fontStyle: "italic",
          }}
        >
          {subtext}
        </div>
      )}
      {expanded && items.length > 0 && (
        <div
          style={{
            marginTop: 8,
            marginLeft: 16,
            padding: 10,
            background: "#fafafa",
            border: "1px solid #f3f4f6",
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {items.map((it, i) => {
            const dot =
              it.status === "done"
                ? "#10b981"
                : it.status === "error"
                  ? "#dc2626"
                  : "#d4d4d8";
            return (
              <div
                key={it.question_id || i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 11,
                  color: "#374151",
                }}
                title={it.error_text || it.text}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: dot,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontWeight: 700, color: "#9ca3af" }}>Q{i + 1}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {truncate(it.text, 90)}
                </span>
                {it.status === "error" && (
                  <span style={{ color: "#dc2626", fontSize: 10, fontWeight: 700 }}>error</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}


// Validation step - review/edit the AI-detected brand + competitor architecture
// before generating the analysis. What you save here is the source of truth for
// every metric (brand universe, L1/L2/L3 counting, prominence, competitor set).

const _archInput = {
  padding: "6px 9px", borderRadius: 8, border: "1px solid var(--border)",
  fontSize: 13, minWidth: 150, flex: 1,
};

// Reusable editor for a brand's L2 sub-brands + their L3 products.
function SubBrandsEditor({ subs, onChange }) {
  const setSub = (i, patch) => onChange(subs.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div>
      {subs.length === 0 && (
        <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>
          No sub-brands. Add one if this brand operates sub-brands / product lines.
        </div>
      )}
      {subs.map((s, si) => (
        <div key={si} style={{ border: "1px solid var(--border)", borderRadius: 10,
          padding: 10, marginBottom: 8, background: "#fff" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>L2</span>
            <input value={s.name} onChange={(e) => setSub(si, { name: e.target.value })}
              placeholder="Sub-brand name" style={_archInput} />
            <button type="button" title="Remove sub-brand"
              onClick={() => onChange(subs.filter((_, j) => j !== si))}
              style={{ border: "none", background: "#fee2e2", color: "#b91c1c", borderRadius: 8,
                padding: "5px 9px", cursor: "pointer", fontWeight: 800 }}>✕</button>
          </div>
          <div style={{ paddingLeft: 26 }}>
            {(s.products || []).map((p, pi) => (
              <div key={pi} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: "#64748b" }}>L3</span>
                <input value={p}
                  onChange={(e) => setSub(si, { products: s.products.map((y, k) => (k === pi ? e.target.value : y)) })}
                  placeholder="Product name" style={{ ..._archInput, minWidth: 140 }} />
                <button type="button" title="Remove product"
                  onClick={() => setSub(si, { products: s.products.filter((_, k) => k !== pi) })}
                  style={{ border: "none", background: "#f1f5f9", color: "#475569", borderRadius: 8,
                    padding: "4px 8px", cursor: "pointer" }}>✕</button>
              </div>
            ))}
            <button type="button"
              onClick={() => setSub(si, { products: [...(s.products || []), ""] })}
              style={{ border: "1px dashed var(--border)", background: "#fff", borderRadius: 8,
                padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#475569" }}>
              + Add product
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...subs, { name: "", products: [] }])}
        style={{ border: "1px dashed var(--border)", background: "#fff", borderRadius: 8,
          padding: "5px 11px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#334155" }}>
        + Add sub-brand
      </button>
    </div>
  );
}


function ArchitectureReview({ runId, showToast, mode }) {
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");
  const [l1, setL1] = useState("");
  const [subs, setSubs] = useState([]);
  const [competitors, setCompetitors] = useState([]); // [{ l1, sub_brands:[{name,products}] }]
  const [overflow, setOverflow] = useState([]); // competitors found but below the cap
  const [masterAliases, setMasterAliases] = useState([]); // carried through, drives counting
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRunArchitecture(runId, mode)
      .then((d) => {
        if (cancelled) return;
        const a = d?.architecture || {};
        setSource(d?.source || "");
        setL1(a.l1 || "");
        setMasterAliases(a.aliases || []);
        // Carry aliases (from the entity resolver) through untouched so counting merges variants.
        const mapSub = (s) => ({ name: s.name || "", aliases: [...(s.aliases || [])], products: [...(s.products || [])] });
        setSubs((a.sub_brands || []).map(mapSub));
        setCompetitors((a.competitors || []).map((c) => ({
          l1: c.l1 || "",
          aliases: [...(c.aliases || [])],
          sub_brands: (c.sub_brands || []).map(mapSub),
        })));
        setOverflow(a.competitors_overflow || []);
      })
      .catch((e) => { if (!cancelled) showToast?.(e?.message || "Failed to load architecture", "error"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, mode]);

  const touch = () => { setDirty(true); setSaved(false); };
  const setSubsT = (v) => { setSubs(v); touch(); };
  const setCompsT = (v) => { setCompetitors(v); touch(); };
  const setComp = (i, patch) => setCompsT(competitors.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const save = async () => {
    setSaving(true);
    const clean = (arr) => (arr || [])
      .map((s) => ({ name: s.name.trim(), aliases: s.aliases || [],
                     products: (s.products || []).map((x) => x.trim()).filter(Boolean) }))
      .filter((s) => s.name);
    const architecture = {
      l1: l1.trim(),
      aliases: masterAliases || [],
      sub_brands: clean(subs),
      competitors: competitors
        .map((c) => ({ l1: (c.l1 || "").trim(), aliases: c.aliases || [], sub_brands: clean(c.sub_brands) }))
        .filter((c) => c.l1),
    };
    try {
      await saveRunArchitecture(runId, architecture);
      setSource("edited"); setDirty(false); setSaved(true);
      showToast?.("Architecture saved - it will drive the analysis metrics", "success");
    } catch (e) {
      showToast?.(e?.message || "Failed to save architecture", "error");
    } finally { setSaving(false); }
  };

  const card = {
    background: "#fff", borderRadius: 14, border: "1px solid var(--border)",
    boxShadow: "0 10px 30px rgba(15,23,42,0.06)", padding: 18,
  };
  const srcLabel = source === "edited" ? "user-edited" : source === "derived" ? "AI-detected" : "none";

  if (loading) return <div style={{ color: "#64748b", fontSize: 13, padding: 16 }}>Loading architecture…</div>;

  return (
    <div>
      {/* Master brand */}
      <div style={card}>
        <div style={{ fontWeight: 900, color: "#1e293b", marginBottom: 4 }}>🧩 Your brand{l1 ? ` - ${l1}` : ""}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
          Source: <b>{srcLabel}</b>. Edit to match reality - this drives brand-universe, L1/L2/L3 counting and prominence.
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontWeight: 800, fontSize: 13, minWidth: 130 }}>L1 - Master brand</span>
          <input value={l1} onChange={(e) => { setL1(e.target.value); touch(); }}
            placeholder="e.g. Eximtur" style={{ ..._archInput, minWidth: 200 }} />
        </label>
        <div style={{ fontWeight: 800, fontSize: 13, margin: "6px 0" }}>L2 - Sub-brands &amp; L3 - Products</div>
        <SubBrandsEditor subs={subs} onChange={setSubsT} />
      </div>

      {/* Competitors */}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <div style={{ fontWeight: 900, color: "#1e293b" }}>🥊 Competitors ({competitors.length})</div>
          <button type="button" onClick={() => setCompsT([...competitors, { l1: "", sub_brands: [] }])}
            className="btn-primary" style={{ padding: "6px 12px", borderRadius: 9, fontWeight: 800, fontSize: 12.5 }}>
            + Add competitor
          </button>
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          These brands are the competitive set counted across every metric. Add, delete, or edit each one&apos;s architecture.
        </div>
        {competitors.length === 0 && (
          <div style={{ color: "#94a3b8", fontSize: 12.5 }}>No competitors detected. Add the brands you compete with.</div>
        )}
        {competitors.map((c, ci) => (
          <div key={ci} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 12,
            marginBottom: 12, background: "#f8fafc" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#475569" }}>L1</span>
              <input value={c.l1} onChange={(e) => setComp(ci, { l1: e.target.value })}
                placeholder="Competitor brand name" style={{ ..._archInput, fontWeight: 700, minWidth: 200 }} />
              <button type="button" title="Remove competitor"
                onClick={() => setCompsT(competitors.filter((_, j) => j !== ci))}
                style={{ border: "none", background: "#fee2e2", color: "#b91c1c", borderRadius: 8,
                  padding: "6px 12px", cursor: "pointer", fontWeight: 800, fontSize: 12.5 }}>Delete</button>
            </div>
            <div style={{ paddingLeft: 4 }}>
              <SubBrandsEditor subs={c.sub_brands} onChange={(v) => setComp(ci, { sub_brands: v })} />
            </div>
          </div>
        ))}
        {overflow.length > 0 && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10,
            background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12.5, color: "#92400e" }}>
            We also identified <b>{overflow.join(", ")}</b>, but they were not visible
            enough in the answers to be considered competitors for this question set. Add any
            of them above if you want them counted.
          </div>
        )}
      </div>

      {/* Save bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
        <button type="button" onClick={save} disabled={saving || !dirty} className="btn-primary"
          style={{ padding: "9px 18px", borderRadius: 10, fontWeight: 900, fontSize: 13,
            opacity: (saving || !dirty) ? 0.55 : 1 }}>
          {saving ? "Saving…" : "Save architecture"}
        </button>
        {saved && <span style={{ color: "#16a34a", fontSize: 12.5, fontWeight: 700 }}>✓ Saved - will drive the metrics</span>}
        {dirty && !saved && <span style={{ color: "#b45309", fontSize: 12.5 }}>Unsaved changes - Save before generating</span>}
      </div>
    </div>
  );
}


// Step 3 - Validation. Its own wizard step between Audit review and Analysis.
function ValidationStage({ run, onReload, showToast, handle409, onBack }) {
  const [advancing, setAdvancing] = useState(false);
  // Already-validated runs skip the choice (the saved architecture loads regardless).
  const [mode, setMode] = useState(run?.edited_architecture_json ? "automated" : null);

  // Manual vs Automated choice - shown once before the architecture loads.
  if (!mode) {
    const Card = ({ onClick, icon, title, tag, tagColor, accent, children }) => {
      const [hover, setHover] = useState(false);
      return (
        <div role="button" tabIndex={0} onClick={onClick}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
          onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
          style={{
            flex: 1, display: "flex", flexDirection: "column", cursor: "pointer",
            border: `1px solid ${hover ? accent : "var(--border)"}`, borderRadius: 16,
            padding: "22px 24px", background: "#fff",
            boxShadow: hover ? `0 12px 34px ${accent}22` : "0 1px 2px rgba(15,23,42,0.04)",
            transition: "box-shadow .15s, border-color .15s",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, fontSize: 24,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `${accent}14` }}>{icon}</div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16.5, color: "#0f172a", lineHeight: 1.1 }}>{title}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: tagColor, textTransform: "uppercase",
                letterSpacing: "0.06em", marginTop: 3 }}>{tag}</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.6 }}>{children}</div>
        </div>
      );
    };
    return (
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ fontWeight: 900, fontSize: 18, color: "#1e293b", marginBottom: 4 }}>
          Step 3 - Validation: how should we build the architecture?
        </div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}>
          The brand &amp; competitor architecture you confirm here is the <b>sole source of truth</b>
          {" "}for every metric in the analysis. Pick how to start:
        </div>
        <div style={{ display: "flex", gap: 18, alignItems: "stretch" }}>
          <Card onClick={() => setMode("automated")} icon="🤖" title="Automated"
            tag="AI-detected" tagColor="#2563eb" accent="#2563eb">
            Reads the audit answers and detects your brand and competitors on all three levels
            (L1 master, L2 sub-brands, L3 products), merges name variants, and drops
            non-competing products.
            <span style={{ display: "block", marginTop: 10 }}>You then review &amp; edit.</span>
          </Card>
          <Card onClick={() => setMode("manual")} icon="✍️" title="Manual"
            tag="Start blank" tagColor="#64748b" accent="#0f172a">
            Only your brand (L1) is pre-filled. You add competitors and products by hand.
            <span style={{ display: "block", marginTop: 10 }}>Note: a brand you add that never
            appeared in the answers will simply count 0.</span>
          </Card>
        </div>
        <div style={{ marginTop: 20 }}>
          <button type="button" onClick={onBack}
            style={{ border: "1px solid var(--border)", background: "#fff", borderRadius: 10,
              padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#334155" }}>
            ← Back to Audit
          </button>
        </div>
      </div>
    );
  }

  const startAnalysis = async () => {
    setAdvancing(true);
    try {
      try {
        await analysisRunTransitionStatus(run.id, "running_analysis");
      } catch (e) {
        const ALREADY_RUNNING = new Set([
          "running_analysis", "analysis_complete", "generating_pptx", "ready", "delivered",
        ]);
        let liveStatus = "";
        try { const fresh = await analysisRunGet(run.id); liveStatus = fresh?.status || ""; } catch { /* ignore */ }
        if (!ALREADY_RUNNING.has(liveStatus)) throw e;
      }
      showToast("Analysis started", "success");
      await onReload();
    } catch (e) {
      if (!handle409(e)) showToast(e?.message || "Could not start analysis", "error");
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ background: "linear-gradient(120deg,#eef2ff,#fff)", border: "1px solid var(--border)",
        borderRadius: 14, padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 900, color: "#1e293b" }}>
            Step 3 - Validation {mode === "manual" ? "(Manual)" : "(Automated)"}
          </div>
          <button type="button" onClick={() => setMode(null)}
            style={{ border: "1px solid var(--border)", background: "#fff", color: "#334155",
              borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 11.5, fontWeight: 700 }}>
            Change setup
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: "#475569", marginTop: 3 }}>
          {mode === "manual"
            ? "Add your competitors and their products by hand. Whatever you Save here is the source of truth for every metric."
            : "Review the AI-detected brand & competitor architecture. Whatever you Save here is the source of truth for every metric - then generate the analysis."}
        </div>
      </div>

      <ArchitectureReview runId={run.id} showToast={showToast} mode={mode} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 22, gap: 12 }}>
        <button type="button" onClick={onBack}
          style={{ border: "1px solid var(--border)", background: "#fff", borderRadius: 10,
            padding: "9px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#334155" }}>
          ← Back to Audit
        </button>
        <button type="button" onClick={startAnalysis} disabled={advancing} className="btn-primary"
          style={{ padding: "10px 20px", borderRadius: 10, fontWeight: 900, fontSize: 13.5,
            opacity: advancing ? 0.6 : 1 }}>
          {advancing ? "Starting…" : "Generate Analysis →"}
        </button>
      </div>
    </div>
  );
}


function AuditStage({ run, brand, onReload, showToast, handle409, onProceedToValidation }) {
  const [advancing, setAdvancing] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [runStatuses, setRunStatuses] = useState({}); // rid -> {model, status, completed, total}
  const autoAdvancedRef = React.useRef(false);

  const isRunning = run.status === "running_audit";
  const isComplete = run.status === "audit_complete";

  // Fetch matrix on mount + whenever audit_id changes. When status is
  // running_audit, also poll each non-terminal run and refresh the matrix
  // as cells complete. Auto-transitions to audit_complete when every run
  // reaches a terminal state so downstream gates become available.
  useEffect(() => {
    if (!run.audit_id) return;
    let cancelled = false;
    const intervals = {};

    const refreshMatrix = async () => {
      try {
        const m = await fetchAuditMatrix(run.audit_id);
        if (!cancelled) setMatrix(m ?? { audit: null, matrix: { columns: [], rows: [] } });
        return m;
      } catch {
        return null;
      }
    };

    (async () => {
      setMatrixLoading(true);
      const m = await refreshMatrix();
      setMatrixLoading(false);

      const cols = m?.matrix?.columns || [];
      const seed = {};
      for (const c of cols) {
        seed[c.run_id] = {
          model: c.model || "model",
          status: c.run_status || (isRunning ? "running" : "done"),
          completed: 0,
          total: 0,
        };
      }
      if (!cancelled) setRunStatuses(seed);

      // Only poll if the audit is actively running. In audit_complete we
      // show the matrix as-is without hammering the backend.
      if (!isRunning) return;

      for (const c of cols) {
        const rid = c.run_id;
        const initial = seed[rid];
        if (!initial) continue;
        if (["done", "error", "cancelled"].includes(initial.status)) continue;

        intervals[rid] = setInterval(async () => {
          try {
            const s = await fetchRunStatus(rid);
            if (cancelled) return;
            setRunStatuses((prev) => ({
              ...prev,
              [rid]: {
                ...(prev[rid] || {}),
                status: s.status || prev[rid]?.status,
                completed: s.completed ?? prev[rid]?.completed ?? 0,
                total: s.total ?? prev[rid]?.total ?? 0,
                // LLM187: per-question detail surfaces
                last_completed: s.last_completed ?? null,
                in_flight: s.in_flight || [],
                items: s.items || [],
              },
            }));
            if (["done", "error", "cancelled"].includes(s.status)) {
              clearInterval(intervals[rid]);
              delete intervals[rid];
              await refreshMatrix();
            }
          } catch {
            /* transient; try again next tick */
          }
        }, 1200);
      }
    })();

    return () => {
      cancelled = true;
      Object.values(intervals).forEach(clearInterval);
    };
  }, [run.audit_id, isRunning]);

  // Derived flags from runStatuses for header chips + button enablement
  const statusesList = Object.entries(runStatuses);
  const doneCount = statusesList.filter(([, i]) => i.status === "done").length;
  const terminalCount = statusesList.filter(([, i]) =>
    ["done", "error", "cancelled"].includes(i.status)
  ).length;
  const allTerminal = statusesList.length > 0 && terminalCount === statusesList.length;

  // Auto-advance: when every run reaches a terminal state while still in
  // running_audit, flip the backend to audit_complete so the Generate
  // Analysis gate becomes available. Guarded against re-fire by a ref.
  // Race: backend's `_maybe_advance_to_audit_complete` ALSO transitions
  // server-side. With auto_advance the chain continues past audit_complete
  // into running_analysis before our POST lands. Treat the rejection as
  // success when the live status is at or past audit_complete.
  useEffect(() => {
    if (!isRunning || !allTerminal || autoAdvancedRef.current) return;
    autoAdvancedRef.current = true;
    (async () => {
      try {
        await analysisRunTransitionStatus(run.id, "audit_complete");
        await onReload();
      } catch (e) {
        const ALREADY_PAST = new Set([
          "audit_complete", "running_analysis", "analysis_complete",
          "generating_pptx", "ready", "delivered",
        ]);
        let liveStatus = "";
        try {
          const fresh = await analysisRunGet(run.id);
          liveStatus = fresh?.status || "";
        } catch {
          /* ignore */
        }
        try {
          await onReload();
        } catch {
          /* ignore */
        }
        if (ALREADY_PAST.has(liveStatus)) {
          // Backend got there first - silent no-op.
          return;
        }
        autoAdvancedRef.current = false;
        showToast(e?.message || "Could not mark audit complete", "error");
      }
    })();
  }, [isRunning, allTerminal, run.id, onReload, showToast]);

  // Stage 2 -> Stage 3 handoff: only do the fast transition here. The actual
  // long-running copilotStart call happens in Stage 3 (AnalysisStage), which
  // renders a cute in-page spinner while the LLM streams. This keeps the
  // user from staring at a frozen "Running…" progress line on Stage 2 for
  // 30+ seconds.
  // Step 2 → Step 3: mark the audit complete (if still running) and advance to the
  // Validation step. Starting the analysis now happens from Validation, not here.
  const proceedToValidation = async () => {
    setAdvancing(true);
    try {
      if (run.status === "running_audit") {
        setProgressMsg("Marking audit complete…");
        try {
          await analysisRunTransitionStatus(run.id, "audit_complete");
        } catch (e) {
          // Backend may have auto-advanced past audit_complete already.
          const ALREADY_PAST = new Set([
            "audit_complete", "running_analysis", "analysis_complete",
            "generating_pptx", "ready", "delivered",
          ]);
          let liveStatus = "";
          try {
            const fresh = await analysisRunGet(run.id);
            liveStatus = fresh?.status || "";
          } catch { /* ignore */ }
          if (!ALREADY_PAST.has(liveStatus)) {
            throw e;
          }
        }
        await onReload();
      }
      onProceedToValidation?.();
    } catch (e) {
      if (!handle409(e)) {
        showToast(e?.message || "Could not proceed to validation", "error");
      }
    } finally {
      setAdvancing(false);
      setProgressMsg("");
    }
  };

  const generateAnalysis = proceedToValidation;   // button handler alias
  const generateDisabled = advancing || (isRunning && doneCount === 0);
  const generateTitle = isRunning
    ? allTerminal
      ? "Review the AI-detected brand architecture, then generate the analysis"
      : doneCount === 0
        ? "Waiting for the first model to finish…"
        : "You can proceed now with partial results, or wait for all models to finish"
    : "Review the AI-detected brand architecture, then generate the analysis";

  return (
    <div className="audit-pane" style={{ marginTop: 0 }}>
      <PreviousStagesSummary run={run} brand={brand} currentStageKey="audit" />

      {/* Live run-progress panel. Visible while the audit is still executing
          so the user can watch each model tick over - the matrix below
          populates cell-by-cell as runs finish. */}
      {isRunning && statusesList.length > 0 && (
        <div
          className="audit-card"
          style={{
            borderRadius: 18,
            border: "1px solid var(--border)",
            background: "#fff",
            boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
            overflow: "hidden",
            marginBottom: 16,
          }}
        >
          <div
            className="audit-header"
            style={{
              padding: "14px 16px",
              background: "linear-gradient(120deg, rgba(0,0,0,0.94), rgba(17,24,39,0.90))",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 950 }}>
              Audit running -{" "}
              <span style={{ color: "#ffcc33" }}>{brand?.name || "brand"}</span>
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.78)", fontWeight: 800 }}>
              {doneCount} / {statusesList.length} done
            </div>
          </div>
          <div className="run-status-wrap" style={{ padding: "14px 16px" }}>
            <ul className="run-status-list" style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {statusesList.map(([rid, info]) => (
                <RunStatusRow key={rid} rid={rid} info={info} />
              ))}
            </ul>
          </div>
        </div>
      )}

      {matrixLoading && (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "#6b7280",
            fontSize: 13,
          }}
        >
          Loading answer matrix…
        </div>
      )}

      {matrix && (matrix?.matrix?.rows || []).length > 0 && (
        <div
          className="audit-card"
          style={{
            borderRadius: 18,
            border: "1px solid var(--border)",
            background: "#fff",
            boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
            overflow: "hidden",
            maxWidth: "100%",
            width: "100%",
          }}
        >
          <div
            className="audit-header"
            style={{
              padding: "14px 16px",
              background: "linear-gradient(120deg, rgba(255,204,51,0.96), rgba(0,0,0,0.92))",
              color: "#111827",
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 950 }}>
                Answer matrix{isRunning ? " - populating live" : ""}
              </div>
              <div style={{ fontSize: 12, color: "rgba(17,24,39,0.75)", fontWeight: 800 }}>
                {(matrix?.matrix?.rows || []).length} questions ×{" "}
                {(matrix?.matrix?.columns || []).length} models
              </div>
            </div>
            <button
              type="button"
              onClick={generateAnalysis}
              disabled={generateDisabled}
              className="btn-primary"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                fontWeight: 900,
                fontSize: 12,
              }}
              title={generateTitle}
            >
              {advancing ? "Working\u2026" : "Proceed to Validation \u2192"}
            </button>
          </div>
          <div className="matrix-wrap" style={{ padding: 16 }}>
            <AuditMatrix
              audit={matrix?.audit}
              matrix={matrix?.matrix}
              onRefresh={async () => {
                try {
                  const m = await fetchAuditMatrix(run.audit_id);
                  setMatrix(m);
                } catch (e) {
                  showToast(e?.message || "Failed to refresh matrix", "error");
                }
              }}
            />
          </div>
        </div>
      )}

      {/* ---- Approval bar (matches the matrix card's full width) ---- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginTop: 16,
          padding: "14px 18px",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          maxWidth: "100%",
          width: "100%",
          boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          {isRunning
            ? doneCount === 0
              ? "Audit in progress. Wait for at least one model to finish, or abort from the top bar."
              : `Audit still running (${doneCount}/${statusesList.length} models done). You can generate now with partial results, or wait for all to finish.`
            : "Audit complete. Review the answers above and approve to generate the brand analysis."}
        </div>
        {advancing && progressMsg && (
          <div style={styles.progressBar}>{progressMsg}</div>
        )}
        <button
          type="button"
          onClick={generateAnalysis}
          disabled={generateDisabled}
          className="btn-primary"
          style={{ padding: "10px 16px", borderRadius: 12, fontWeight: 900 }}
          title={generateTitle}
        >
          {advancing ? "Working…" : "Proceed to Validation →"}
        </button>
      </div>
    </div>
  );
}


// -----------------------------------------------------------
// Stage 3 - Analysis Review
// -----------------------------------------------------------

function AnalysisStage({ run, brand, onReload, showToast, handle409 }) {
  const [advancing, setAdvancing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // LLM184: Revise modal state
  const [revisePreflightOpen, setRevisePreflightOpen] = useState(false);
  const [reviseProposals, setReviseProposals] = useState([]);
  const [reviseLoading, setReviseLoading] = useState(false);
  // Branch state - distinct loading flag so the Revise button doesn't
  // freeze when the branch fetch is in flight.
  const [branchLoading, setBranchLoading] = useState(false);

  const isRunning = run.status === "running_analysis";

  const onBranch = async () => {
    if (branchLoading) return;
    const ok = window.confirm(
      "Create a new analysis run from the same audit?\n\n" +
      "Your current analysis stays untouched. The new run is added to this " +
      "brand's history with a link back to this one as its source. Same audit " +
      "answers, fresh insights under the current methodology.",
    );
    if (!ok) return;
    setBranchLoading(true);
    try {
      const { analysisRunBranch } = await import("../services/api");
      const result = await analysisRunBranch(run.id);
      const newId = result?.id;
      if (!newId) throw new Error("Branch did not return a run id");
      // Hand off to the new run - it's already running_analysis on the
      // backend, so the wizard opens directly to the running Stage 3.
      window.location.href = `/brands/${run.brand_id}/new-analysis?runId=${newId}`;
    } catch (e) {
      showToast(e?.message || "Could not branch this run", "error");
      setBranchLoading(false);
    }
  };

  const onOpenRevise = async () => {
    setReviseLoading(true);
    try {
      // Re-run alias preflight so the user sees fresh proposals before
      // committing to a revision. The proposed list comes from the LLM and
      // already excludes existing aliases.
      const data = await analysisRunAliasPreflight(run.id);
      setReviseProposals(
        (data.proposed_aliases || []).map((p) => ({ ...p, accepted: true })),
      );
      setRevisePreflightOpen(true);
    } catch (e) {
      showToast(e?.message || "Could not run alias pre-flight", "error");
    } finally {
      setReviseLoading(false);
    }
  };

  const onConfirmRevise = async ({ saveToLibrary } = { saveToLibrary: false }) => {
    const accepted = reviseProposals.filter((p) => p.accepted).map((p) => p.alias);
    const rejected = reviseProposals.filter((p) => !p.accepted).map((p) => p.alias);
    setRevisePreflightOpen(false);
    setAdvancing(true);
    try {
      // Optional: also push the accepted aliases into the brand library
      // (Pivot D - same opt-in semantics as the initial alias preflight).
      if (saveToLibrary && accepted.length > 0) {
        try {
          await Promise.all(accepted.map((a) => addBrandAlias(run.brand_id, a)));
        } catch {
          // Non-fatal - the revise itself is the main action
        }
      }

      await analysisRunRevise(run.id, {
        acceptedAliases: accepted,
        rejectedAliases: rejected,
      });
      // Stage 3 re-mounts in running_analysis with the new session; the
      // backend kicks the LLM as a background task and the wizard polls for
      // status. Extraction cache keeps the rerun fast.
      showToast("Revision started - regenerating analysis…", "success");
      await onReload();
    } catch (e) {
      showToast(e?.message || "Revise failed", "error");
    } finally {
      setAdvancing(false);
    }
  };

  // While the run is in running_analysis, poll for status updates. The backend
  // kicks off the LLM analysis as a background task on the running_analysis
  // transition (see routes/analysis_runs.py), so the wizard no longer makes
  // a long blocking HTTP request - it just polls. When status flips to
  // analysis_complete or failed, reload to render the appropriate panel.
  useEffect(() => {
    if (!isRunning) return;
    let cancelled = false;
    const tick = async () => {
      try {
        await onReload();
      } catch {
        /* transient - try again next tick */
      }
    };
    const id = setInterval(() => {
      if (!cancelled) tick();
    }, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, run.id]);

  // Live node counter - poll the generation progress faster than the status poll
  // so "12 / 24 sections" ticks up smoothly on the loading screen.
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    if (!isRunning) { setProgress(null); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const p = await analysisRunProgress(run.id);
        if (!cancelled) setProgress(p);
      } catch { /* transient */ }
    };
    poll();
    const id = setInterval(() => { if (!cancelled) poll(); }, 1500);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, run.id]);

  const advance = async () => {
    setAdvancing(true);
    try {
      await analysisRunTransitionStatus(run.id, "generating_pptx");
      showToast("PPTX generation started", "success");
      await onReload();
    } catch (e) {
      if (!handle409(e)) {
        showToast(e?.message || "Could not advance", "error");
      }
    } finally {
      setAdvancing(false);
    }
  };

  // --- Loading state: LLM is streaming the analysis ---
  if (isRunning) {
    return (
      <AnalysisGeneratingPanel
        error={null}
        analysisStartedAt={run.analysis_started_at}
        progress={progress}
      />
    );
  }

  // --- Reviewable state: analysis_complete ---
  return (
    <div className="audit-pane" style={{ marginTop: 0 }}>
      <PreviousStagesSummary run={run} brand={brand} currentStageKey="analysis" />

      <div
        className="audit-card"
        style={{
          borderRadius: 18,
          border: "1px solid var(--border)",
          background: "#fff",
          boxShadow: "0 16px 44px rgba(15,23,42,0.12)",
          overflow: "hidden",
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <div
          className="audit-header"
          style={{
            padding: "14px 16px",
            background: "linear-gradient(120deg, rgba(255,204,51,0.96), rgba(0,0,0,0.92))",
            color: "#111827",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 950 }}>Analysis ready</div>
            {run.validator_status && <ValidatorBadge status={run.validator_status} />}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* LLM184: Revise - re-runs alias preflight + regenerates the
                narrative. Extraction cache reuses the prior map-phase output
                so the rerun is fast. */}
            <button
              type="button"
              onClick={onOpenRevise}
              disabled={advancing || reviseLoading}
              className="btn-secondary"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                fontWeight: 800,
                fontSize: 12,
              }}
              title="Adjust aliases and regenerate this analysis"
            >
              {reviseLoading ? "Loading…" : "↻ Revise"}
            </button>
            {/* Branch - re-analyse the same audit under the current
                methodology. Preserves the original report; new run
                is created as a child of this one. */}
            <button
              type="button"
              onClick={onBranch}
              disabled={advancing || branchLoading}
              className="btn-secondary"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                fontWeight: 800,
                fontSize: 12,
              }}
              title="Create a new analysis run from the same audit (preserves this report; new methodology applied)"
            >
              {branchLoading ? "Branching…" : "⎇ Branch"}
            </button>
            <button
              type="button"
              onClick={advance}
              disabled={advancing}
              className="btn-primary"
              style={{
                padding: "8px 14px",
                borderRadius: 10,
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              {advancing ? "Generating PPTX…" : "Generate PPTX →"}
            </button>
          </div>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
            Your brand analysis is ready. Review the 10 sections and the Counting
            Methods comparison. If numbers look off, you can revise aliases and
            re-run - we reuse the audit so revision is fast.
          </div>

          {run.copilot_session_id ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <a
                href={`/analysis/${run.copilot_session_id}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  padding: "14px 20px",
                  borderRadius: 14,
                  background: "linear-gradient(135deg, rgba(0,0,0,0.95), rgba(17,24,39,0.85))",
                  color: "#ffcc33",
                  fontWeight: 900,
                  fontSize: 14,
                  textDecoration: "none",
                  border: "1px solid #ffcc33",
                  boxShadow: "0 10px 28px rgba(15,23,42,0.18)",
                  transition: "transform 0.12s ease, box-shadow 0.12s ease",
                  letterSpacing: "0.01em",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 14px 32px rgba(15,23,42,0.22)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 10px 28px rgba(15,23,42,0.18)";
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 3h7v7" />
                  <path d="M10 14L21 3" />
                  <path d="M21 14v7h-7" />
                  <path d="M3 10V3h7" />
                </svg>
                See your analysis report
                <span style={{ fontSize: 16, lineHeight: 1, marginLeft: 2 }}>→</span>
              </a>

              <button
                type="button"
                onClick={() => setChatOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  color: "#111827",
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: "pointer",
                  transition: "border-color 0.12s ease, transform 0.12s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#ffcc33";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "none";
                }}
                title="Ask follow-up questions or request edits to any section"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Chat with MAD Pilot
              </button>
            </div>
          ) : (
            <div
              style={{
                padding: 12,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: 8,
                fontSize: 12,
                color: "#9a3412",
              }}
            >
              No analysis session linked. This run may have errored - try again
              from the Audit stage.
            </div>
          )}
        </div>
      </div>

      <CopilotDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        brandId={run.brand_id}
        auditId={run.audit_id}
        initialSessionId={run.copilot_session_id}
      />

      {/* LLM184: Revise modal - reuses AliasPreflightModal, but the
          confirm path goes through analysisRunRevise instead of just
          writing aliases. */}
      {revisePreflightOpen && (
        <AliasPreflightModal
          proposals={reviseProposals}
          setProposals={setReviseProposals}
          onClose={() => setRevisePreflightOpen(false)}
          onConfirm={onConfirmRevise}
        />
      )}
    </div>
  );
}


// Cute animated "analysis cooking" panel shown while copilotStart is in flight.
//
// `analysisStartedAt` is an ISO-8601 timestamp from the backend (set when
// the run transitions to running_analysis). When the user navigates away
// and comes back mid-run, we derive the current step from elapsed wall-clock
// time instead of resetting to step 1. The animation is still cosmetic
// (the backend doesn't emit per-substep events), but at least it now
// reflects roughly how far along the run actually is.
function AnalysisGeneratingPanel({ error, analysisStartedAt, progress }) {
  // Each step has an estimated duration (in seconds). The bar advances
  // smoothly within the active step's window, and the step list anchors
  // the user mentally to "where in the pipeline we are." Total expected
  // budget = sum of durations ≈ 60s for a typical run; if the run takes
  // longer the bar caps at 95% until the backend status flips us out.
  const steps = useMemo(
    () => [
      { label: "Reading audit answers", duration: 5 },
      { label: "Counting brand mentions & sources", duration: 6 },
      { label: "Identifying brands & competitors", duration: 14 },
      { label: "Validating numbers against the matrix", duration: 5 },
      { label: "Composing the analysis report", duration: 18 },
      { label: "Finishing up", duration: 6 },
    ],
    [],
  );

  const totalBudget = useMemo(
    () => steps.reduce((s, x) => s + x.duration, 0),
    [steps],
  );

  // Re-tick every 500ms for a smooth bar without burning render cycles.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const elapsedSec = useMemo(() => {
    if (!analysisStartedAt) return 0;
    const startMs = Date.parse(analysisStartedAt);
    if (Number.isNaN(startMs)) return 0;
    return Math.max(0, (now - startMs) / 1000);
  }, [analysisStartedAt, now]);

  const stepIdx = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < steps.length; i++) {
      acc += steps[i].duration;
      if (elapsedSec < acc) return i;
    }
    return steps.length - 1;
  }, [elapsedSec, steps]);

  const pctRaw = totalBudget > 0 ? (elapsedSec / totalBudget) * 100 : 0;
  // Cap at 95% so the bar doesn't claim "done" before the backend
  // actually flips status; the parent un-mounts us when it does.
  const pctTime = Math.min(95, Math.round(pctRaw));

  // Real node counter from the backend (data extractors + per-section calls).
  // When present it drives the bar + the "N / total" readout; otherwise we fall
  // back to the time-based estimate above.
  const realTotal = Number(progress?.total) || 0;
  const realDone = Number(progress?.done) || 0;
  const hasReal = realTotal > 0;
  const phaseLabel =
    progress?.phase === "extracting" ? "Analysing audit data"
      : progress?.phase === "writing_sections" ? "Writing report sections"
        : progress?.phase === "starting" ? "Starting up"
          : "";
  // Cap at 99% so the bar never reads 100% before the status flips us out.
  const pct = hasReal ? Math.min(99, Math.round((realDone / realTotal) * 100)) : pctTime;
  const remainingSec = Math.max(0, totalBudget - elapsedSec);
  const remainingLabel =
    elapsedSec > totalBudget
      ? "almost done"
      : remainingSec >= 60
        ? `~${Math.round(remainingSec / 60)} min remaining`
        : `~${Math.round(remainingSec)}s remaining`;

  return (
    <div className="audit-pane" style={{ marginTop: 0 }}>
      <style>{`
        @keyframes nap-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(220%); }
        }
        @keyframes nap-active-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
      <div
        className="audit-card"
        style={{
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          background: "#ffffff",
          boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
          overflow: "hidden",
          maxWidth: 640,
          width: "100%",
          margin: "0 auto",
          padding: 28,
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#6b7280",
              }}
            >
              Stage 3
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "#0f172a",
                margin: "2px 0 0",
                letterSpacing: "-0.01em",
              }}
            >
              Generating analysis
            </h2>
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {Math.round(elapsedSec)}s elapsed · {remainingLabel}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              height: 8,
              borderRadius: 999,
              background: "#f1f5f9",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: 999,
                background: "linear-gradient(90deg, #1e293b 0%, #0f172a 100%)",
                transition: "width 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Moving shimmer to indicate "alive" even if % hasn't moved */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "40%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,204,51,0.55) 50%, transparent 100%)",
                  animation: "nap-shimmer 2.4s linear infinite",
                }}
              />
            </div>
          </div>
          <div
            style={{
              marginTop: 6,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "#9ca3af",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>{pct}%</span>
            <span>
              {hasReal
                ? `${realDone} / ${realTotal} nodes${phaseLabel ? ` · ${phaseLabel}` : ""}`
                : `Step ${Math.min(stepIdx + 1, steps.length)} of ${steps.length}`}
            </span>
          </div>
        </div>

        {/* Step list */}
        <ul
          style={{
            listStyle: "none",
            margin: "20px 0 4px",
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {steps.map((s, i) => {
            const state = i < stepIdx ? "done" : i === stepIdx ? "active" : "pending";
            return (
              <li
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 4px",
                  fontSize: 13,
                  color: state === "pending" ? "#9ca3af" : "#1f2937",
                  fontWeight: state === "active" ? 700 : 500,
                  animation:
                    state === "active" ? "nap-active-pulse 1.6s ease-in-out infinite" : undefined,
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      state === "done"
                        ? "#10b981"
                        : state === "active"
                          ? "#ffcc33"
                          : "transparent",
                    border:
                      state === "pending" ? "1.5px solid #d1d5db" : "none",
                    color: state === "done" || state === "active" ? "#ffffff" : "#9ca3af",
                    fontSize: 11,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {state === "done" ? "✓" : ""}
                </span>
                <span>{s.label}</span>
              </li>
            );
          })}
        </ul>

        <div
          style={{
            marginTop: 18,
            padding: "10px 12px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            fontSize: 11.5,
            color: "#64748b",
            lineHeight: 1.55,
          }}
        >
          You can leave this page - the run keeps going on the server. If a
          transient network blip happens, we automatically retry once before
          failing. You'll find the run waiting in the Analysis Hub.
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              fontSize: 12,
              color: "#991b1b",
            }}
          >
            <strong>Analysis failed:</strong> {error}
          </div>
        )}
      </div>
    </div>
  );
}


function ValidatorBadge({ status }) {
  const bg = status === "green" ? "#d1fae5" : status === "yellow" ? "#fef3c7" : "#fee2e2";
  const col = status === "green" ? "#065f46" : status === "yellow" ? "#92400e" : "#991b1b";
  return (
    <span style={{
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      background: bg,
      color: col,
      fontSize: 11,
      fontWeight: 900,
      textTransform: "uppercase",
    }}>
      Quality: {status === "green" ? "Passed" : status === "yellow" ? "Warnings" : "Issues"}
    </span>
  );
}


// -----------------------------------------------------------
// Stage 4 - Deliverable
// -----------------------------------------------------------

function DeliverableStage({ run, brand, onReload, showToast }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [latest, setLatest] = useState(null);
  const [versions, setVersions] = useState([]); // LLM179: full version history
  const [versionsExpanded, setVersionsExpanded] = useState(false);
  const [downloadingVer, setDownloadingVer] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [marking, setMarking] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingLatest(true);
      try {
        const [l, list] = await Promise.all([
          analysisRunPptxLatest(run.id).catch(() => null),
          analysisRunPptxArtifacts(run.id).catch(() => ({ items: [] })),
        ]);
        setLatest(l);
        setVersions(list?.items || []);
      } finally {
        setLoadingLatest(false);
      }
    })();
  }, [run.id, run.status]);

  // Auto-reload while PPTX is being generated in the background. Poll the
  // analysis_run every 2s; the background task flips the status to "ready"
  // (or "failed") when it finishes, which routes away from this effect.
  useEffect(() => {
    if (run.status !== "generating_pptx") return;
    const interval = setInterval(() => {
      onReload();
    }, 2000);
    return () => clearInterval(interval);
  }, [run.status, onReload]);

  const download = async () => {
    try {
      const blob = await analysisRunPptxDownload(run.id);
      const url = URL.createObjectURL(blob);
      const dl = document.createElement("a");
      dl.href = url;
      dl.download = `MAD_Analysis_${run.id}.pptx`;
      dl.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || "Download failed", "error");
    }
  };

  // LLM179: download a specific historical PPTX version.
  const downloadVersion = async (versionNo) => {
    setDownloadingVer(versionNo);
    try {
      const { blob, filename } = await analysisRunPptxVersionDownload(run.id, versionNo);
      const url = URL.createObjectURL(blob);
      const dl = document.createElement("a");
      dl.href = url;
      dl.download = filename || `MAD_Analysis_${run.id}_v${versionNo}.pptx`;
      dl.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e?.message || "Download failed", "error");
    } finally {
      setDownloadingVer(null);
    }
  };

  // LLM179: regenerate produces a NEW version row (auto-incremented), not
  // an overwrite. Triggers ready -> generating_pptx; the existing
  // BackgroundTask in pptx_generation.py handles the rest.
  const regenerate = async () => {
    if (!run || run.status !== "ready") return;
    if (!window.confirm("Regenerate the PPTX? A new version will be added; older versions stay available.")) return;
    setRegenerating(true);
    try {
      await analysisRunTransitionStatus(run.id, "generating_pptx");
      showToast("Regenerating PPTX…", "success");
      await onReload();
    } catch (e) {
      showToast(e?.message || "Regenerate failed", "error");
    } finally {
      setRegenerating(false);
    }
  };

  const markDelivered = async () => {
    setMarking(true);
    try {
      await analysisRunTransitionStatus(run.id, "delivered");
      showToast("Marked as delivered", "success");
      await onReload();
    } catch (e) {
      showToast(e?.message || "Could not mark delivered", "error");
    } finally {
      setMarking(false);
    }
  };

  const isGenerating = run.status === "generating_pptx";
  const isReady = run.status === "ready";
  const isDelivered = run.status === "delivered";

  return (
    <div style={styles.card}>
      <PreviousStagesSummary run={run} brand={brand} currentStageKey="deliverable" />

      <SectionHeading>Deliverable</SectionHeading>
      <div style={{ fontSize: 14, marginBottom: 10 }}>
        Current status: <b>{run.status}</b>
      </div>

      {isGenerating && (
        <div style={styles.hint}>
          PPTX is being generated in the background. Reload status in a few
          seconds to see the artifact.
        </div>
      )}

      {loadingLatest ? (
        <div style={styles.hint}>Loading artifact…</div>
      ) : latest ? (
        <div style={styles.artifactCard}>
          <div style={{ fontWeight: 900 }}>
            Version {latest.version_no}
            {latest.file_size_bytes ? (
              <span style={{ color: "#6b7280", fontWeight: 600, fontSize: 12 }}>
                {" "}({Math.round(latest.file_size_bytes / 1024)} KB)
              </span>
            ) : null}
            {versions.length > 1 && (
              <button
                type="button"
                onClick={() => setVersionsExpanded((v) => !v)}
                style={{
                  marginLeft: 12,
                  background: "none",
                  border: "none",
                  color: "#1e3a8a",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  textDecoration: "underline dotted",
                  padding: 0,
                }}
              >
                {versionsExpanded
                  ? "Hide history"
                  : `${versions.length - 1} older version${versions.length === 2 ? "" : "s"}`}
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Generated {latest.generated_at ? new Date(latest.generated_at).toLocaleString() : ""}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button onClick={download} style={styles.primaryBtn}>
              Download PPTX
            </button>
            {isReady && (
              <button
                onClick={regenerate}
                disabled={regenerating}
                style={styles.secondaryBtn}
                title="Generate a fresh PPTX as a new version (older versions stay available)"
              >
                {regenerating ? "Regenerating…" : "↻ Regenerate"}
              </button>
            )}
          </div>

          {versionsExpanded && versions.length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid #f3f4f6", paddingTop: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto auto auto", rowGap: 8, columnGap: 12, alignItems: "center", fontSize: 12 }}>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Version</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Notes</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Generated</div>
                <div style={{ fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>Size</div>
                <div />
                {versions.map((v) => (
                  <React.Fragment key={v.id}>
                    <div style={{ fontWeight: 800, color: "#111827" }}>
                      v{v.version_no}
                      {latest?.version_no === v.version_no && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#166534" }}>· latest</span>
                      )}
                    </div>
                    <div style={{ color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.notes || "-"}
                    </div>
                    <div style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                      {v.generated_at ? new Date(v.generated_at).toLocaleString() : "-"}
                    </div>
                    <div style={{ color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                      {v.file_size_bytes != null ? `${(Number(v.file_size_bytes) / 1024).toFixed(1)} KB` : "-"}
                    </div>
                    <button
                      onClick={() => downloadVersion(v.version_no)}
                      disabled={downloadingVer === v.version_no}
                      className="btn-secondary"
                      style={{ padding: "4px 10px", fontSize: 11, borderRadius: 8, fontWeight: 700 }}
                    >
                      {downloadingVer === v.version_no ? "…" : "Download"}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={styles.hint}>
          No PPTX generated yet.{" "}
          {isGenerating ? "Waiting for background task…" : null}
        </div>
      )}

      {run.copilot_session_id && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "28px 16px 8px",
          }}
        >
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            title="Ask follow-up questions or request edits to the analysis"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              padding: "16px 28px",
              borderRadius: 16,
              background: "linear-gradient(135deg, rgba(17,24,39,0.96) 0%, rgba(0,0,0,0.94) 100%)",
              color: "#ffcc33",
              border: "1px solid #ffcc33",
              fontSize: 15,
              fontWeight: 900,
              letterSpacing: "0.01em",
              cursor: "pointer",
              boxShadow: "0 16px 36px rgba(15,23,42,0.22), 0 0 0 0 rgba(255,204,51,0.35)",
              transition: "transform 0.14s ease, box-shadow 0.14s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 20px 44px rgba(15,23,42,0.28), 0 0 0 4px rgba(255,204,51,0.18)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow =
                "0 16px 36px rgba(15,23,42,0.22), 0 0 0 0 rgba(255,204,51,0.35)";
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Chat with MAD Pilot about this analysis
            <span style={{ fontSize: 18, lineHeight: 1 }}>→</span>
          </button>
        </div>
      )}

      <div style={styles.bottomBar}>
        <button onClick={onReload} style={styles.secondaryBtn}>Reload</button>
        {isReady && !isDelivered && (
          <button onClick={markDelivered} disabled={marking} style={styles.primaryBtn}>
            {marking ? "Marking…" : "Mark as Delivered"}
          </button>
        )}
        {isDelivered && (
          <div style={{ ...styles.hint, alignSelf: "center", margin: 0 }}>
            <b>Delivered</b> on{" "}
            {run.delivered_at ? new Date(run.delivered_at).toLocaleString() : "-"}
          </div>
        )}
      </div>

      <CopilotDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        brandId={run.brand_id}
        auditId={run.audit_id}
        initialSessionId={run.copilot_session_id}
      />
    </div>
  );
}


// -----------------------------------------------------------
// Shared atoms + styles
// -----------------------------------------------------------

function SectionHeading({ children }) {
  return <div style={styles.sectionHeading}>{children}</div>;
}

const GOLD = "#d4a017";
const BLACK = "#111827";

const styles = {
  page: { padding: "24px 32px", maxWidth: 1400, margin: "0 auto", color: BLACK },
  header: { marginBottom: 16 },
  title: {
    fontSize: 26, fontWeight: 950, margin: 0, color: BLACK,
    letterSpacing: "-0.01em",
  },
  titleDivider: { color: "#9ca3af", fontWeight: 400, margin: "0 4px" },
  titleBrand: { color: GOLD, fontWeight: 950 },
  subtitle: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 12, color: "#6b7280", marginTop: 8, flexWrap: "wrap",
  },
  subtitleDot: { color: "#d1d5db", fontWeight: 700 },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
    transition: "background 0.12s ease, border-color 0.12s ease",
  },
  iconBtn: {
    width: 28, height: 28, borderRadius: 7,
    border: "1px solid #e5e7eb", background: "#fff",
    color: "#374151",
    cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: 0,
    transition: "background 0.12s ease, border-color 0.12s ease",
  },
  loading: { padding: 40, textAlign: "center", color: "#6b7280" },
  error: { padding: 40, textAlign: "center", color: "#b91c1c" },

  stepper: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "16px 20px",
    background: "#fafafa",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    marginBottom: 18,
  },
  stepActive: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: BLACK,
    fontWeight: 900,
    flex: "0 0 auto",
    padding: "6px 10px",
  },
  stepDone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#065f46",
    fontWeight: 800,
    flex: "0 0 auto",
    padding: "6px 10px",
  },
  stepPending: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#9ca3af",
    fontWeight: 600,
    flex: "0 0 auto",
    padding: "6px 10px",
  },
  stepNumber: {
    width: 32, height: 32, borderRadius: 999,
    background: "#fff", border: `2px solid ${GOLD}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, marginBottom: 4,
  },
  stepLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" },
  stepLine: { flex: 1, height: 2, background: "#e5e7eb" },
  stepLineDone: { flex: 1, height: 2, background: GOLD },

  card: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
    padding: 22, boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
  },
  lockBanner: {
    padding: 14, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 12,
    marginBottom: 14, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center",
  },
  dismissBtn: {
    background: "#fff", border: "1px solid #e5e7eb", padding: "6px 12px",
    borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700,
  },

  sectionHeading: {
    fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
    color: "#374151", fontWeight: 900, marginTop: 18, marginBottom: 10,
  },
  hint: { fontSize: 12, color: "#6b7280", lineHeight: 1.5, marginBottom: 10 },
  empty: { fontSize: 12, color: "#6b7280", padding: 14, background: "#fafafa", borderRadius: 8 },

  pillGrid: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 },
  pill: {
    padding: "10px 14px", borderRadius: 12, border: "1px solid #e5e7eb",
    background: "#fff", cursor: "pointer", fontSize: 12, textAlign: "left",
    color: BLACK,
  },
  pillActive: {
    padding: "10px 14px", borderRadius: 12, border: `1px solid ${GOLD}`,
    background: "#fffbe8", cursor: "pointer", fontSize: 12, textAlign: "left",
    color: BLACK, boxShadow: `0 0 0 2px ${GOLD}22`,
  },

  questionsList: { listStyle: "none", padding: 0, margin: "0 0 10px 0", display: "flex", flexDirection: "column", gap: 6 },
  questionLoaded: {
    display: "flex", gap: 10, padding: 8, background: "#fafafa",
    borderRadius: 8, fontSize: 13, alignItems: "center",
  },
  questionExtra: { display: "flex", gap: 8, alignItems: "center" },

  input: {
    padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8,
    fontSize: 13, color: BLACK, background: "#fff", fontFamily: "inherit",
  },
  iconBtnDanger: {
    width: 28, height: 28, borderRadius: 7,
    border: "1px solid #fecaca", background: "#fff5f5", color: "#b91c1c",
    cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: 0,
    transition: "background 0.12s ease, border-color 0.12s ease",
  },
  iconBtnSubtle: {
    width: 28, height: 28, borderRadius: 6,
    border: "1px solid #e5e7eb", background: "#fff", color: "#374151",
    cursor: "pointer", fontSize: 14, lineHeight: 1,
  },
  fieldLabel: {
    display: "block",
    fontSize: 11, fontWeight: 900, color: "#374151",
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, marginTop: 4,
  },
  aliasPill: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 4px 4px 10px", borderRadius: 999,
    background: "#fffbe8", border: `1px solid ${GOLD}`,
    fontSize: 12, fontWeight: 700, color: BLACK,
  },
  aliasPillRemove: {
    width: 20, height: 20, borderRadius: 999,
    border: "none", background: "transparent",
    color: "#6b7280", cursor: "pointer", fontSize: 14, lineHeight: 1,
    padding: 0,
  },
  providerLabel: {
    fontSize: 10, fontWeight: 900, color: "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.05em",
    marginBottom: 4,
  },
  modelPill: {
    display: "inline-flex", alignItems: "center",
    padding: "4px 10px", borderRadius: 999,
    border: "1px solid #e5e7eb", background: "#fff",
    fontSize: 11, fontWeight: 600, color: BLACK, cursor: "pointer",
  },
  modelPillActive: {
    display: "inline-flex", alignItems: "center",
    padding: "4px 10px", borderRadius: 999,
    border: `1px solid ${GOLD}`, background: "#fffbe8",
    fontSize: 11, fontWeight: 700, color: BLACK, cursor: "pointer",
  },

  bottomBar: {
    marginTop: 22, paddingTop: 16, borderTop: "1px solid #e5e7eb",
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
  },
  summary: { fontSize: 13, color: "#374151" },
  primaryBtn: {
    padding: "12px 18px", borderRadius: 12, border: `1px solid ${GOLD}`,
    background: BLACK, color: GOLD, fontWeight: 900, cursor: "pointer", fontSize: 13,
  },
  secondaryBtn: {
    padding: "10px 14px", borderRadius: 10, border: "1px solid #e5e7eb",
    background: "#fff", color: BLACK, fontWeight: 700, cursor: "pointer", fontSize: 12,
  },
  disabledBtn: {
    padding: "12px 18px", borderRadius: 12, border: "1px solid #e5e7eb",
    background: "#f3f4f6", color: "#9ca3af", fontWeight: 900,
    cursor: "not-allowed", fontSize: 13,
  },
  progressBar: {
    marginTop: 14, padding: 10,
    background: "#fffbe8", border: `1px solid ${GOLD}`, borderRadius: 8,
    fontSize: 12, color: BLACK, fontWeight: 700,
  },

  modalBackdrop: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 14, padding: 24,
    maxWidth: 520, width: "90%", maxHeight: "80vh", overflow: "auto",
    boxShadow: "0 20px 60px rgba(15,23,42,0.3)",
  },

  artifactCard: {
    background: "#fafafa", border: "1px solid #e5e7eb",
    borderRadius: 10, padding: 14, marginTop: 10,
  },
};
